// 📄 src/pages/AutoReplyBuilder/components/AutoReplyNodeEditor.jsx

import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { Combobox } from "@headlessui/react";
import { ArrowDown, ArrowUp, Plus, X, Upload, Trash2 } from "lucide-react";
import { Button } from "../../../components/ui/button";
import WhatsAppTemplatePreview from "./WhatsAppTemplatePreview";
import WhatsAppFlowNodeEditor from "./WhatsAppFlowNodeEditor";
import WhatsAppFlowDynamicOptionsEditor from "./WhatsAppFlowDynamicOptionsEditor";
import SchedulingBookingEditor from "./SchedulingBookingEditor";
import ConfirmBookingEditor from "./ConfirmBookingEditor";
import PrepareCommerceServiceEditor from "./PrepareCommerceServiceEditor";
import DecisionTableEditor from "./DecisionTableEditor";
import AutoReplyFollowUpEditor, {
  emptyFollowUp,
  isFollowUpEligibleNode,
  selectAutoReplyFollowUpMaxHorizonSeconds,
  validateFollowUp,
} from "./AutoReplyFollowUpEditor";
import axiosClient from "../../../api/axiosClient";
import { catalogApi } from "../../../api/catalogApi";
import { useAuth } from "../../../app/providers/AuthProvider";
import {
  normalizeChoiceTerms,
  validateChoiceConfig,
  WHATSAPP_INTERACTIVE_OPTION_MAX,
  WHATSAPP_LIST_ROW_DESCRIPTION_LIMIT,
} from "../utils/choiceValidation";
import {
  defaultInvalidResponse,
  INPUT_CAPTURE_TYPES,
  isInputCaptureRequestContextCompatible,
  validateInputCaptureConfig,
} from "../utils/inputCaptureValidation";
import { getCustomFieldDefinitions } from "../../CustomFields/customFieldsApi";
import {
  CONDITION_MAX_RULES,
  CONDITION_TYPES,
  createConditionConfig,
  createConditionRule,
  getInputCaptureConditionFields,
  operatorsForConditionType,
} from "../utils/conditionValidation";
import {
  createDecisionTableConfig,
  normalizeDecisionTableConfig,
  validateDecisionTableConfig,
} from "../utils/decisionTableValidation";
import {
  BROWSE_CATALOGUE_DEFAULT_PROMPT,
  BROWSE_CATALOGUE_MAXIMUM_PROMPT_LENGTH,
  BROWSE_CATALOGUE_PRESENTATIONS,
  validateBrowseCatalogueConfig,
} from "../utils/browseCatalogueValidation";
import {
  getUpstreamBrowseCatalogueNodes,
  getSearchCatalogueInputFields,
  SEARCH_CATALOGUE_DEFAULT_MAXIMUM_RESULTS,
  SEARCH_CATALOGUE_MAXIMUM_RESULTS,
  SEARCH_CATALOGUE_MINIMUM_RESULTS,
  SEARCH_CATALOGUE_MODES,
  validateSearchCatalogueConfig,
} from "../utils/searchCatalogueValidation";
import {
  createPresentCatalogueCommerceConfig,
  findSearchCatalogueNode,
  getSearchCatalogueNodes,
  PRESENT_CATALOGUE_COMMERCE_DEFAULT_QUANTITY_PROMPT,
  PRESENT_CATALOGUE_COMMERCE_MAXIMUM_QUANTITY_PROMPT_LENGTH,
  PRESENT_CATALOGUE_COMMERCE_QUANTITY_MODES,
  PRESENT_CATALOGUE_COMMERCE_TRANSACTION_TYPES,
  PRESENT_CATALOGUE_RESULTS_DEFAULT_PROMPT,
  PRESENT_CATALOGUE_RESULTS_MAXIMUM_PROMPT_LENGTH,
  PRESENT_CATALOGUE_SECTION_MODES,
  validatePresentCatalogueResultsConfig,
} from "../utils/presentCatalogueResultsValidation";
import {
  getReviewInputCaptureFields,
  getReviewDecisionTablePriceOutputs,
  REVIEW_AND_SUBMIT_DEFAULT_PROMPT,
  REVIEW_AND_SUBMIT_MAXIMUM_BINDINGS,
  REVIEW_AND_SUBMIT_MAXIMUM_LABEL_LENGTH,
  REVIEW_AND_SUBMIT_MAXIMUM_PROMPT_LENGTH,
  validateReviewAndSubmitConfig,
} from "../utils/reviewAndSubmitValidation";
import {
  getUrlCtaHostname,
  normalizeUrlCtaConfig,
  URL_CTA_SCHEMA_VERSION,
  validateUrlCtaConfig,
} from "../utils/urlCtaValidation";

const CUSTOM_CONDITION_FIELD = "__custom_condition_field__";

const isValidHttpsUrl = value => {
  const raw = String(value || "").trim();
  if (!raw) return false;
  try {
    const u = new URL(raw);
    return u.protocol === "https:";
  } catch {
    return false;
  }
};

const isLikelyMetaMediaId = value => {
  const raw = String(value || "").trim();
  if (raw.length < 6) return false;
  if (/\s/.test(raw) || raw.includes(":")) return false;
  if (/^https?:\/\//i.test(raw)) return false;
  return true;
};

const isValidTemplateHeaderMediaReference = value => {
  const raw = String(value || "").trim();
  if (!raw) return false;
  if (isValidHttpsUrl(raw)) return true;

  const prefixed = raw.match(/^(handle|id):(.+)$/i);
  if (!prefixed) return false;

  return isLikelyMetaMediaId(prefixed[2]);
};

const mediaTypeForHeaderKind = headerKind => {
  const kind = String(headerKind || "").trim().toLowerCase();
  if (kind === "image") return "image";
  if (kind === "video") return "video";
  if (kind === "document") return "document";
  return "";
};

const acceptForHeaderKind = headerKind => {
  const kind = mediaTypeForHeaderKind(headerKind);
  if (kind === "image") return "image/jpeg,image/png,image/webp";
  if (kind === "video") return "video/mp4,video/3gpp";
  if (kind === "document") return ".pdf,application/pdf";
  return "";
};

const countBodyPlaceholders = body => {
  if (!body) return 0;
  const s = String(body);
  const positional = s.match(/\{\{\s*\d+\s*\}\}/g) || [];
  const named = s.match(/\{\{\s*\}\}/g) || [];
  return positional.length + named.length;
};

const normalizeTemplateSummary = template => {
  const id = String(template?.id ?? template?.Id ?? "").trim();
  const name = String(template?.name ?? template?.Name ?? "").trim();
  const languageCode = String(
    template?.languageCode ??
      template?.LanguageCode ??
      template?.language ??
      template?.Language ??
      "en_US"
  ).trim();

  return {
    id: id || null,
    name,
    languageCode: languageCode || "en_US",
  };
};

const parseTemplateButtons = template => {
  const rawButtons =
    template?.buttons ??
    template?.buttonParams ??
    template?.ButtonParams ??
    template?.multiButtons ??
    template?.MultiButtons ??
    template?.urlButtons ??
    template?.UrlButtons ??
    null;

  if (Array.isArray(rawButtons)) return rawButtons;

  if (typeof rawButtons === "string" && rawButtons.trim().startsWith("[")) {
    try {
      const parsed = JSON.parse(rawButtons);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  return [];
};

const normalizeTemplateDetail = template => {
  const buttonParams = parseTemplateButtons(template);
  const headerKind = String(
    template?.headerKind ??
      template?.HeaderKind ??
      (template?.hasImageHeader || template?.HasImageHeader ? "image" : "none")
  )
    .trim()
    .toLowerCase();

  return {
    name: template?.name ?? template?.Name ?? "",
    language:
      template?.language ??
      template?.Language ??
      template?.languageCode ??
      template?.LanguageCode ??
      "en_US",
    body: template?.body ?? template?.Body ?? template?.bodyText ?? "",
    placeholderCount:
      template?.placeholderCount ??
      template?.PlaceholderCount ??
      template?.bodyVarCount ??
      template?.BodyVarCount ??
      0,
    headerKind,
    requiresHeaderMediaUrl:
      template?.requiresHeaderMediaUrl === true ||
      template?.RequiresMediaHeader === true ||
      headerKind === "image" ||
      headerKind === "video" ||
      headerKind === "document",
    buttonParams,
    multiButtons: buttonParams,
  };
};

const getEditorDraftKey = node => {
  const nodeId = String(node?.id || "").trim();
  const nodeType = String(node?.type || "").trim();
  if (!nodeId || !nodeType) return null;
  return `xbytechat:auto-reply:node-editor-draft:${nodeId}:${nodeType}`;
};

const readEditorDraft = draftKey => {
  if (!draftKey || typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(draftKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const writeEditorDraft = (draftKey, payload) => {
  if (!draftKey || typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(draftKey, JSON.stringify(payload));
  } catch {
    // ignore storage write failures (quota/privacy)
  }
};

const clearEditorDraft = draftKey => {
  if (!draftKey || typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(draftKey);
  } catch {
    // ignore
  }
};

export default function AutoReplyNodeEditor({
  node,
  flowNodes = [],
  flowEdges = [],
  sourceFlowId = null,
  autoReplyFlowOptions = [],
  onClose,
  onSave,
}) {
  const { businessId: ctxBusinessId } = useAuth();
  const [form, setForm] = useState({
    text: "",
    promptText: "Please choose an option:",
    options: [],
    inputKey: "buttonText",
    pathMap: {},
    templateName: "",
    tags: [],
    seconds: 10, // 🔁 default wait = 10s
    body: "",
    schemaVersion: URL_CTA_SCHEMA_VERSION,
    buttonLabel: "Visit Website",
    url: "",
    afterSend: "continue",
    multiButtons: [],
    // Template dynamic values (CTA-like)
    headerMediaUrl: "",
    bodyParams: [],
    urlButtonParams: ["", "", ""],
    useProfileName: false,
    profileNameSlot: 1,
    // 🆕 CTA flow fields
    ctaFlowConfigId: "",
    ctaFlowName: "",
    ackText: "",
    // Standalone media message
    mediaType: "image",
    mediaId: "",
    mediaUrl: "",
    caption: "",
    filename: "",
    // Header media for button_choice
    headerMedia: null,
    question: "Please enter your response:",
    fieldKey: "custom.response",
    inputType: "text",
    invalidResponseMessage: "Please enter a valid response.",
    maximumAttempts: 3,
    minimum: "",
    maximum: "",
    minimumLength: "",
    maximumLength: "",
    requestContextFieldDefinitionId: "",
    comparisonType: "string",
    rules: createConditionConfig().rules,
    defaultPathId: "default",
    decisionTable: createDecisionTableConfig(),
    queryFieldKey: "",
    categorySourceNodeId: "",
    catalogId: "",
    maximumResults: SEARCH_CATALOGUE_DEFAULT_MAXIMUM_RESULTS,
    presentation: "auto",
    searchNodeId: "",
    reviewDetailBindings: [],
    targetFlowId: null,
  });

  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateQuery, setTemplateQuery] = useState("");
  const [followUp, setFollowUp] = useState(emptyFollowUp);
  const [followUpMaxHorizonSeconds, setFollowUpMaxHorizonSeconds] = useState(0);
  const [branchUnsupportedConfig, setBranchUnsupportedConfig] = useState(false);
  const [catalogOptions, setCatalogOptions] = useState([]);
  const [catalogOptionsLoading, setCatalogOptionsLoading] = useState(false);
  const [catalogOptionsError, setCatalogOptionsError] = useState("");
  const [requestContextFields, setRequestContextFields] = useState([]);
  const [requestContextFieldsError, setRequestContextFieldsError] = useState("");
  const availableConditionFields = useMemo(
    () => getInputCaptureConditionFields(flowNodes),
    [flowNodes]
  );
  const normalizedConditionFieldKey = String(form.fieldKey || "").trim().toLowerCase();
  const selectedConditionField = availableConditionFields.find(
    field => field.fieldKey === normalizedConditionFieldKey
  );
  const conditionFieldSelection = selectedConditionField?.fieldKey || CUSTOM_CONDITION_FIELD;
  const conditionFieldTypeMismatch = selectedConditionField &&
    !selectedConditionField.comparisonTypes.includes(form.comparisonType);
  const availableSearchFields = useMemo(
    () => getSearchCatalogueInputFields(flowNodes),
    [flowNodes]
  );
  const normalizedSearchFieldKey = String(form.queryFieldKey || "").trim().toLowerCase();
  const selectedSearchField = availableSearchFields.find(
    field => field.fieldKey === normalizedSearchFieldKey
  );
  const availableBrowseSources = useMemo(
    () =>
      getUpstreamBrowseCatalogueNodes(
        flowNodes,
        flowEdges,
        node?.id
      ),
    [flowEdges, flowNodes, node?.id]
  );
  const selectedBrowseSource = availableBrowseSources.find(
    source =>
      source.id === String(form.categorySourceNodeId || "").trim()
  );
  const availableSearchNodes = useMemo(
    () => getSearchCatalogueNodes(flowNodes),
    [flowNodes]
  );
  const selectedSearchNode = findSearchCatalogueNode(
    flowNodes,
    form.searchNodeId
  );
  const availableReviewFields = useMemo(
    () => getReviewInputCaptureFields(flowNodes, flowEdges, node?.id),
    [flowEdges, flowNodes, node?.id]
  );

  const updateConditionComparisonType = comparisonType => {
    const allowed = operatorsForConditionType(comparisonType);
    setForm(prev => ({
      ...prev,
      comparisonType,
      rules: (prev.rules || []).map(rule => ({
        ...rule,
        operator: allowed.includes(rule.operator) ? rule.operator : "equals",
      })),
    }));
  };

  const selectConditionField = value => {
    if (value === CUSTOM_CONDITION_FIELD) {
      setForm(prev => ({ ...prev, fieldKey: "" }));
      return;
    }

    const field = availableConditionFields.find(option => option.fieldKey === value);
    if (!field) return;
    const allowed = operatorsForConditionType(field.comparisonType);
    setForm(prev => ({
      ...prev,
      fieldKey: field.fieldKey,
      comparisonType: field.comparisonType,
      rules: (prev.rules || []).map(rule => ({
        ...rule,
        operator: allowed.includes(rule.operator) ? rule.operator : "equals",
      })),
    }));
  };
  const initializedNodeSessionRef = useRef(null);
  const hydratedRef = useRef(false);

  // 🆕 CTA flow list for dropdown
  const [ctaFlows, setCtaFlows] = useState([]);

  const isTemplateNode = node?.type === "template";
  const isMessageNode = node?.type === "message";
  const isInputCaptureNode = node?.type === "input_capture";
  const isMediaNode = node?.type === "media" || node?.type === "media_message";
  const isButtonChoiceNode =
    node?.type === "button_choice" || node?.type === "choice";
  const isUrlCtaNode = node?.type === "url_cta";
  const isBranchNode = node?.type === "branch" || node?.type === "condition";
  const isDecisionTableNode = node?.type === "decision_table";
  const isBrowseCatalogueNode = node?.type === "browse_catalogue";
  const isSearchCatalogueNode = node?.type === "search_catalogue";
  const isPresentCatalogueResultsNode =
    node?.type === "present_catalogue_results";
  const isReviewAndSubmitNode = node?.type === "review_and_submit";
  const isPrepareCommerceServiceNode = node?.type === "prepare_commerce_service";
  const isTagNode = node?.type === "tag";
  const isLeadTemperatureNode = node?.type === "set_lead_temperature";
  const isWaitNode = node?.type === "wait";
  const isCtaNode = node?.type === "cta_flow";
  const isHandoverNode = node?.type === "handover_agent";
  const isWhatsAppFlowNode = node?.type === "whatsapp_flow";
  const isDynamicWhatsFlowNode = node?.type === "whatsapp_flow_dynamic_options";
  const isSchedulingBookingNode = node?.type === "schedule_booking";
  const isConfirmBookingNode = node?.type === "confirm_booking";
  const isAddFlowNode = node?.type === "add_flow";
  const followUpEligible = useMemo(
    () => isFollowUpEligibleNode(node, flowEdges, flowNodes),
    [node, flowEdges, flowNodes]
  );
  const followUpValidationError = followUpEligible
    ? validateFollowUp(followUp, followUpMaxHorizonSeconds)
    : "";
  useEffect(() => {
    if (!isInputCaptureNode) return undefined;
    let cancelled = false;
    setRequestContextFieldsError("");
    getCustomFieldDefinitions({ entityType: "REQUEST_CONTEXT", includeInactive: false })
      .then(fields => {
        if (!cancelled)
          setRequestContextFields((Array.isArray(fields) ? fields : []).filter(field => field.isActive !== false));
      })
      .catch(() => {
        if (!cancelled) {
          setRequestContextFields([]);
          setRequestContextFieldsError("Request Context fields could not be loaded.");
        }
      });
    return () => { cancelled = true; };
  }, [isInputCaptureNode]);
  const eligibleAddFlowOptions = useMemo(
    () =>
      (Array.isArray(autoReplyFlowOptions) ? autoReplyFlowOptions : []).filter(
        flow =>
          flow?.isEligible !== false &&
          (!sourceFlowId ||
            String(flow?.id || "").toLowerCase() !==
              String(sourceFlowId).toLowerCase())
      ),
    [autoReplyFlowOptions, sourceFlowId]
  );
  const selectedAddFlowTarget = useMemo(() => {
    const targetFlowId = String(form.targetFlowId || "").trim().toLowerCase();
    if (!targetFlowId) return null;
    return (Array.isArray(autoReplyFlowOptions) ? autoReplyFlowOptions : []).find(
      flow => String(flow?.id || "").trim().toLowerCase() === targetFlowId
    ) || null;
  }, [autoReplyFlowOptions, form.targetFlowId]);
  const addFlowTargetUnavailable = Boolean(form.targetFlowId) &&
    (!selectedAddFlowTarget || selectedAddFlowTarget.isEligible === false);

  useEffect(() => {
    if (!isSearchCatalogueNode && !isBrowseCatalogueNode) return undefined;

    let cancelled = false;
    setCatalogOptionsLoading(true);
    setCatalogOptionsError("");
    catalogApi
      .listAllCatalogs({ activeOnly: true })
      .then(items => {
        if (cancelled) return;
        setCatalogOptions(
          (Array.isArray(items) ? items : []).filter(item => item?.isActive !== false)
        );
      })
      .catch(() => {
        if (!cancelled) {
          setCatalogOptions([]);
          setCatalogOptionsError("Active catalogues could not be loaded.");
        }
      })
      .finally(() => {
        if (!cancelled) setCatalogOptionsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isBrowseCatalogueNode, isSearchCatalogueNode, node?.id]);

  const headerKind = useMemo(() => {
    const hk = selectedTemplate?.headerKind ?? selectedTemplate?.HeaderKind ?? "none";
    return String(hk || "none").trim().toLowerCase();
  }, [selectedTemplate]);

  const needsHeaderUrl =
    headerKind === "image" || headerKind === "video" || headerKind === "document";

  const headerKindLabel =
    headerKind === "image"
      ? "Image"
      : headerKind === "video"
      ? "Video"
      : headerKind === "document"
      ? "Document"
      : "Media";
  const templateHeaderMediaValue = String(form.headerMediaUrl || "").trim();
  const templateHeaderMediaInvalid =
    isTemplateNode &&
    needsHeaderUrl &&
    !!templateHeaderMediaValue &&
    !isValidTemplateHeaderMediaReference(templateHeaderMediaValue);
  const templateHeaderMediaMissing =
    isTemplateNode && needsHeaderUrl && !templateHeaderMediaValue;
  const templateHeaderMediaError = templateHeaderMediaMissing
    ? `This template requires a ${headerKindLabel.toLowerCase()}. Upload a ${headerKindLabel.toLowerCase()} or paste a public HTTPS URL.`
    : templateHeaderMediaInvalid
    ? "Use a public HTTPS URL or an uploaded media handle."
    : "";

  const bodyPlaceholderCount = useMemo(
    () => countBodyPlaceholders(form.body),
    [form.body]
  );

  const canUseProfile = bodyPlaceholderCount > 0;

  const dynamicUrlButtons = useMemo(() => {
    const btns =
      selectedTemplate?.buttonParams ||
      selectedTemplate?.ButtonParams ||
      selectedTemplate?.multiButtons ||
      form.multiButtons ||
      [];

    return (Array.isArray(btns) ? btns : [])
      .map(b => ({
        type: String(b?.type || b?.Type || "").trim().toUpperCase(),
        subType: String(b?.subType || b?.SubType || "").trim().toLowerCase(),
        text: String(b?.text || b?.Text || "").trim(),
        index:
          typeof b?.index === "number"
            ? b.index
            : typeof b?.Index === "number"
            ? b.Index
            : 0,
        parameterValue: String(b?.parameterValue || b?.ParameterValue || "").trim(),
      }))
      .filter(b => {
        const isUrl = b.type === "URL" || b.subType === "url";
        const isDynamic = b.parameterValue.includes("{{");
        return isUrl && isDynamic;
      })
      .filter(b => b.index >= 0 && b.index <= 2)
      .sort((a, b) => a.index - b.index);
  }, [selectedTemplate, form.multiButtons]);

  // ---- API calls ----

  const businessId = useCallback(() => {
    return (
      ctxBusinessId ||
      localStorage.getItem("businessId") ||
      localStorage.getItem("sa_selectedBusinessId") ||
      null
    );
  }, [ctxBusinessId]);

  const loadApprovedTemplateDetail = useCallback(
    async (selection, templateOptions = templates) => {
      const bizId = businessId();
      const templateName =
        typeof selection === "string"
          ? String(selection).trim()
          : String(selection?.name || "").trim();
      const languageCode =
        typeof selection === "string"
          ? String(
              templateOptions.find(
                template => String(template?.name || "").trim() === templateName
              )?.languageCode || ""
            ).trim()
          : String(selection?.languageCode || selection?.language || "").trim();

      if (!bizId || !templateName) return;

      try {
        const languagePart = languageCode
          ? `?language=${encodeURIComponent(languageCode)}`
          : "";
        const { data } = await axiosClient.get(
          `templates/${bizId}/${encodeURIComponent(templateName)}${languagePart}`
        );

        const rawTemplate = data?.template || data;
        if (rawTemplate?.name || rawTemplate?.Name) {
          const normalized = normalizeTemplateDetail(rawTemplate);
          setSelectedTemplate(normalized);
          setForm(prev => ({
            ...prev,
            templateName: normalized.name,
            body: normalized.body || "",
            multiButtons: normalized.buttonParams || [],
          }));
          return;
        }

        setSelectedTemplate(null);
      } catch (err) {
        console.error("Failed to fetch approved template details", err);
        setSelectedTemplate(null);
      }
    },
    [businessId, templates]
  );

  const loadApprovedTemplates = useCallback(
    async (preselectedName = "") => {
      const bizId = businessId();
      if (!bizId) return;

      try {
        const { data } = await axiosClient.get(`templates/${bizId}`, {
          params: {
            status: "APPROVED",
            page: 1,
            pageSize: 200,
            sortKey: "name",
            sortDir: "asc",
          },
        });

        if (data?.success) {
          const raw = Array.isArray(data.templates) ? data.templates : [];
          const normalized = raw
            .map(normalizeTemplateSummary)
            .filter(template => template.name);
          setTemplates(normalized);
          if (preselectedName) {
            await loadApprovedTemplateDetail(preselectedName, normalized);
          }
          return;
        }

        setTemplates([]);
      } catch (err) {
        console.error("Failed to fetch approved template list", err);
        setTemplates([]);
      }
    },
    [businessId, loadApprovedTemplateDetail]
  );

  const fetchFullTemplate = useCallback(
    async selection => loadApprovedTemplateDetail(selection),
    [loadApprovedTemplateDetail]
  );

  const fetchTemplates = useCallback(
    async (preselectedName = "") => loadApprovedTemplates(preselectedName),
    [loadApprovedTemplates]
  );

  // 🆕 Load all published CTA flows for this business
  const fetchCtaFlows = useCallback(async () => {
    try {
      const { data } = await axiosClient.get("cta-flow/all-published");
      // data is List<VisualFlowSummaryDto> from API
      // { id, flowName, isPublished, createdAt }
      setCtaFlows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("❌ Failed to fetch CTA flows", err);
      setCtaFlows([]);
    }
  }, []);

  // ---- Initialize form from node.data.config ----

  useEffect(() => {
    if (!node) {
      initializedNodeSessionRef.current = null;
      hydratedRef.current = false;
      return;
    }

    const sessionKey = `${node.id}:${node.type}`;
    if (initializedNodeSessionRef.current === sessionKey) {
      // Keep in-progress edits when parent re-renders the same opened node.
      return;
    }
    initializedNodeSessionRef.current = sessionKey;
    hydratedRef.current = false;

    const { config } = node.data || {};
    const draftKey = getEditorDraftKey(node);
    const storedDraft = readEditorDraft(draftKey);

    // 🔢 Safely normalize seconds (can be number or string in config)
    const rawSeconds = config?.seconds;
    let normalizedSeconds = 10; // default

    if (typeof rawSeconds === "number") {
      normalizedSeconds = rawSeconds > 0 ? rawSeconds : 10;
    } else if (typeof rawSeconds === "string" && rawSeconds.trim() !== "") {
      const parsed = Number(rawSeconds);
      normalizedSeconds = Number.isFinite(parsed) && parsed > 0 ? parsed : 10;
    }

    const next = {
      text: config?.text || "",
      promptText:
        config?.promptText ??
        config?.message ??
        (node.type === "browse_catalogue"
          ? BROWSE_CATALOGUE_DEFAULT_PROMPT
          : node.type === "present_catalogue_results"
          ? PRESENT_CATALOGUE_RESULTS_DEFAULT_PROMPT
          : node.type === "review_and_submit"
          ? REVIEW_AND_SUBMIT_DEFAULT_PROMPT
          : "Please choose an option:"),
      options: Array.isArray(config?.options)
        ? config.options.map((opt, index) => ({
            label: String(opt?.label || ""),
            value:
              typeof opt?.value === "string" && opt.value.trim()
                ? opt.value.trim()
                : String(index + 1),
            description: String(opt?.description || ""),
            terms: normalizeChoiceTerms(opt?.terms),
          }))
        : node.type === "button_choice" || node.type === "choice"
        ? [
            { label: "Button 1", value: "1", description: "", terms: [] },
            { label: "Button 2", value: "2", description: "", terms: [] },
          ]
        : [],
      inputKey: config?.inputKey || "buttonText",
      pathMap:
        config?.pathMap &&
        typeof config.pathMap === "object" &&
        !Array.isArray(config.pathMap)
          ? config.pathMap
          : {},
      templateName: config?.templateName || "",
      tags: config?.tags || [],
      ...(node.type === "set_lead_temperature" ? {
        leadTemperature: config?.leadTemperature ?? "Warm",
        allowTemperatureDowngrade: config?.allowTemperatureDowngrade === true,
      } : {}),
      seconds: normalizedSeconds,
      body: config?.body || "",
      schemaVersion: config?.schemaVersion ?? URL_CTA_SCHEMA_VERSION,
      buttonLabel: config?.buttonLabel ?? "Visit Website",
      url: config?.url ?? "",
      afterSend: config?.afterSend ?? "continue",
      multiButtons: config?.multiButtons || [],
      headerMediaUrl: config?.headerMediaUrl || "",
      bodyParams: config?.bodyParams || config?.placeholders || [],
      urlButtonParams: config?.urlButtonParams || ["", "", ""],
      useProfileName: !!(config?.useProfileName || false),
      profileNameSlot: config?.profileNameSlot || 1,
      // 🆕 hydrate CTA fields if present
      ctaFlowConfigId: config?.ctaFlowConfigId || config?.CtaFlowConfigId || "",
      ctaFlowName: config?.ctaFlowName || config?.CtaFlowName || "",
      ackText: config?.ackText || "",
      mediaType: config?.mediaType || "image",
      mediaId: config?.mediaId || "",
      mediaUrl: config?.mediaUrl || "",
      caption: config?.caption || "",
      filename: config?.filename || "",
      // Header media for button_choice
      headerMedia: config?.headerMedia && typeof config.headerMedia === "object"
        ? {
            kind: config.headerMedia.kind || "",
            mediaId: config.headerMedia.mediaId || "",
            mediaUrl: config.headerMedia.mediaUrl || "",
            caption: config.headerMedia.caption || "",
          }
        : null,
      question: config?.question || "Please enter your response:",
      fieldKey:
        node.type === "branch"
          ? config?.fieldKey ?? ""
          : config?.fieldKey || "custom.response",
      inputType: config?.inputType || "text",
      invalidResponseMessage:
        config?.invalidResponseMessage || defaultInvalidResponse(config?.inputType || "text"),
      maximumAttempts: config?.maximumAttempts ?? 3,
      minimum: config?.minimum ?? "",
      maximum: config?.maximum ?? "",
      minimumLength: config?.minimumLength ?? "",
      maximumLength: config?.maximumLength ?? "",
      requestContextFieldDefinitionId: config?.requestContextFieldDefinitionId ?? "",
      comparisonType: config?.comparisonType || "string",
      rules: Array.isArray(config?.rules) ? config.rules.map(rule => ({ ...rule })) : [],
      defaultPathId: config?.defaultPathId || "default",
      decisionTable:
        node.type === "decision_table"
          ? {
              schemaVersion: config?.schemaVersion ?? 1,
              inputs: Array.isArray(config?.inputs) ? config.inputs : [],
              outputs: Array.isArray(config?.outputs) ? config.outputs : [],
              rows: Array.isArray(config?.rows) ? config.rows : [],
            }
          : createDecisionTableConfig(),
      queryFieldKey: config?.queryFieldKey ?? "",
      searchMode: config?.searchMode ?? "filtered",
      categorySourceNodeId: config?.categorySourceNodeId ?? "",
      catalogId: config?.catalogId ?? "",
      maximumResults:
        config?.maximumResults ?? SEARCH_CATALOGUE_DEFAULT_MAXIMUM_RESULTS,
      presentation: config?.presentation ?? "auto",
      searchNodeId: config?.searchNodeId ?? "",
      sectionMode: config?.sectionMode ?? "flat",
      commerceEnabled:
        !!config?.commerce &&
        typeof config.commerce === "object" &&
        !Array.isArray(config.commerce),
      commerceTransactionType:
        config?.commerce?.transactionType ??
        createPresentCatalogueCommerceConfig().transactionType,
      commerceQuantityMode:
        config?.commerce?.quantityMode ??
        createPresentCatalogueCommerceConfig().quantityMode,
      commerceQuantityPrompt:
        config?.commerce?.quantityPrompt ??
        createPresentCatalogueCommerceConfig().quantityPrompt,
      reviewDetailBindings: Array.isArray(config?.detailBindings)
        ? config.detailBindings.map((binding, index) => ({
            sourceInputCaptureNodeId: String(
              binding?.sourceInputCaptureNodeId || ""
            ),
            fieldKey: String(binding?.fieldKey || ""),
            labelOverride: String(binding?.labelOverride || ""),
            sortOrder: Number.isInteger(binding?.sortOrder)
              ? binding.sortOrder
              : index,
          })).sort((left, right) => left.sortOrder - right.sortOrder)
        : [],
      reviewServicePricingSource:
        config?.servicePricing?.source === "DecisionTable"
          ? "DecisionTable"
          : "Catalogue",
      reviewDecisionPriceBinding:
        config?.servicePricing?.source === "DecisionTable"
          ? `${config?.servicePricing?.decisionTableNodeId || ""}|${config?.servicePricing?.fieldKey || ""}`
          : "",
      targetFlowId:
        typeof config?.targetFlowId === "string" && config.targetFlowId.trim()
          ? config.targetFlowId.trim()
          : null,
    };
    const hydratedForm =
      storedDraft?.form &&
      typeof storedDraft.form === "object" &&
      !Array.isArray(storedDraft.form)
        ? { ...next, ...storedDraft.form }
        : next;
    const hydratedOptions = Array.isArray(hydratedForm.options)
      ? hydratedForm.options.map((opt, index) => ({
          label: String(opt?.label || ""),
          value:
            typeof opt?.value === "string" && opt.value.trim()
              ? opt.value.trim()
              : String(index + 1),
          description: String(opt?.description || ""),
          terms: normalizeChoiceTerms(opt?.terms),
        }))
      : [];

    const isLegacyConditionConfig =
      (node.type === "branch" || node.type === "condition") &&
      (!Array.isArray(config?.rules) || node?.data?.legacyNodeType === "condition");

    const hydratedBranchUnsupported =
      typeof storedDraft?.branchUnsupportedConfig === "boolean"
        ? storedDraft.branchUnsupportedConfig
        : isLegacyConditionConfig;

    setBranchUnsupportedConfig(hydratedBranchUnsupported);
    setForm({ ...hydratedForm, options: hydratedOptions });
    setFollowUp(
      storedDraft?.followUp && typeof storedDraft.followUp === "object"
        ? storedDraft.followUp
        : node.data?.followUp && typeof node.data.followUp === "object"
        ? node.data.followUp
        : emptyFollowUp()
    );
    setFollowUpMaxHorizonSeconds(0);
    setTemplateQuery("");

    if (node.type === "template") {
      fetchTemplates(hydratedForm?.templateName || config?.templateName);
      setSelectedTemplate(null); // will be set in fetchFullTemplate
    } else {
      setSelectedTemplate(null);
    }

    if (node.type === "cta_flow") {
      fetchCtaFlows();
    }
    hydratedRef.current = true;
  }, [node, fetchTemplates, fetchCtaFlows]);

  useEffect(() => {
    if (!node || !hydratedRef.current) return;
    const draftKey = getEditorDraftKey(node);
    if (!draftKey) return;
    writeEditorDraft(draftKey, {
      form,
      followUp,
      branchUnsupportedConfig,
      updatedAt: Date.now(),
    });
  }, [node, form, followUp, branchUnsupportedConfig]);

  useEffect(() => {
    if (!node || !followUpEligible) return undefined;
    let cancelled = false;
    Promise.resolve(axiosClient.get("autoreplyflows/follow-up-capabilities"))
      .then(({ data } = {}) => {
        if (!cancelled) {
          const genericMaximum = Number(data?.maxHorizonSeconds) || 0;
          setFollowUpMaxHorizonSeconds(isWhatsAppFlowNode || isSchedulingBookingNode
            ? genericMaximum
            : selectAutoReplyFollowUpMaxHorizonSeconds(node.type, data));
        }
      })
      .catch(() => {
        if (!cancelled) setFollowUpMaxHorizonSeconds(0);
      });
    fetchTemplates();
    return () => { cancelled = true; };
  }, [node, followUpEligible, fetchTemplates, isSchedulingBookingNode, isWhatsAppFlowNode]);

  // Keep bodyParams aligned to placeholder count (index 0 => {{1}})
  useEffect(() => {
    if (!isTemplateNode) return;
    const count = bodyPlaceholderCount;
    setForm(prev => {
      const current = Array.isArray(prev.bodyParams) ? prev.bodyParams : [];
      if (current.length === count) return prev;
      const next = Array.from({ length: count }, (_, i) =>
        String(current[i] ?? "")
      );
      return { ...prev, bodyParams: next };
    });
  }, [isTemplateNode, bodyPlaceholderCount]);

  // Ensure urlButtonParams always has stable length 3 (index 0 => button 1)
  useEffect(() => {
    if (!isTemplateNode) return;
    setForm(prev => {
      const current = Array.isArray(prev.urlButtonParams)
        ? prev.urlButtonParams
        : [];
      if (current.length === 3) return prev;
      const next = Array.from({ length: 3 }, (_, i) => String(current[i] ?? ""));
      return { ...prev, urlButtonParams: next };
    });
  }, [isTemplateNode]);

  // Clamp profileNameSlot when placeholder count changes
  useEffect(() => {
    if (!isTemplateNode) return;
    if (!form.useProfileName || !canUseProfile) {
      if (form.useProfileName) {
        setForm(prev => ({ ...prev, useProfileName: false }));
      }
      return;
    }

    const clamped = Math.max(
      1,
      Math.min(form.profileNameSlot || 1, bodyPlaceholderCount)
    );
    if (clamped !== form.profileNameSlot) {
      setForm(prev => ({ ...prev, profileNameSlot: clamped }));
    }
  }, [
    isTemplateNode,
    canUseProfile,
    bodyPlaceholderCount,
    form.useProfileName,
    form.profileNameSlot,
  ]);

  const filteredTemplates = useMemo(() => {
    const q = String(templateQuery || "").trim().toLowerCase();
    const list = Array.isArray(templates) ? templates : [];
    if (!q) return list;
    return list.filter(t => {
      const name = String(t?.name || "").toLowerCase();
      const lang = String(t?.languageCode || t?.language || "").toLowerCase();
      return name.includes(q) || lang.includes(q);
    });
  }, [templates, templateQuery]);

  const selectedTemplateOption = useMemo(() => {
    const name = String(form.templateName || "").trim();
    if (!name) return null;
    return (
      templates.find(t => String(t?.name || "").trim() === name) || {
        name,
        languageCode: "",
      }
    );
  }, [templates, form.templateName]);

  // ---- Handlers ----

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleTagsChange = e => {
    const raw = e.target.value;
    const tags = raw
      .split(",")
      .map(t => t.trim())
      .filter(Boolean);
    setForm(prev => ({ ...prev, tags }));
  };

  const handleChoiceOptionChange = (index, field, value) => {
    setForm(prev => {
      const next = Array.isArray(prev.options) ? [...prev.options] : [];
      if (!next[index]) {
        next[index] = { label: "", value: "", description: "", terms: [] };
      }
      next[index] = {
        ...next[index],
        [field]: field === "terms" ? normalizeChoiceTerms(value) : value,
      };
      return { ...prev, options: next };
    });
  };

  const handleAddChoiceOption = () => {
    setForm(prev => {
      const next = Array.isArray(prev.options) ? [...prev.options] : [];
      const nextIndex = next.length + 1;
      next.push({
        label: `Button ${nextIndex}`,
        value: String(nextIndex),
        description: "",
        terms: [],
      });
      return { ...prev, options: next };
    });
  };

  const handleDeleteChoiceOption = index => {
    setForm(prev => {
      const next = Array.isArray(prev.options)
        ? prev.options.filter((_, i) => i !== index)
        : [];
      return { ...prev, options: next };
    });
  };

  // ─── Header media handlers for button_choice ───
  const [mediaUploading, setMediaUploading] = useState(false);

  const handleStandaloneMediaUpload = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    const bizId = businessId();
    if (!bizId) return;

    const fd = new FormData();
    fd.append("file", file);
    fd.append("mediaType", form.mediaType);

    try {
      setMediaUploading(true);
      const { data } = await axiosClient.post("/auto-reply-builder/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const mediaId = data?.mediaId || data?.media_id || data?.id || "";
      if (!mediaId) return;

      setForm(prev => ({
        ...prev,
        mediaId,
        mediaUrl: "",
        filename:
          prev.mediaType === "document" && !String(prev.filename || "").trim()
            ? file.name
            : prev.filename,
      }));

      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            sessionStorage.setItem(
              `xbytechat:media-preview:${mediaId}`,
              reader.result
            );
          } catch {
            // Preview falls back to the media content endpoint.
          }
        };
        reader.readAsDataURL(file);
      }
    } catch (err) {
      console.error("Standalone media upload failed", err);
    } finally {
      setMediaUploading(false);
      e.target.value = "";
    }
  };

  const handleHeaderMediaKindChange = kind => {
    if (!kind || kind === "none") {
      setForm(prev => ({ ...prev, headerMedia: null }));
    } else {
      setForm(prev => ({
        ...prev,
        headerMedia: {
          kind,
          mediaId: prev.headerMedia?.kind === kind ? prev.headerMedia?.mediaId || "" : "",
          mediaUrl: prev.headerMedia?.kind === kind ? prev.headerMedia?.mediaUrl || "" : "",
          caption: prev.headerMedia?.kind === kind ? prev.headerMedia?.caption || "" : "",
        },
      }));
    }
  };

  const handleHeaderMediaUpload = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    const bizId = businessId();
    if (!bizId) return;

    const fd = new FormData();
    fd.append("file", file);
    fd.append("mediaType", form.headerMedia?.kind || "");

    try {
      setMediaUploading(true);
      const { data } = await axiosClient.post("/auto-reply-builder/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const mediaId = data?.mediaId || data?.media_id || data?.id || "";
      if (mediaId) {
        setForm(prev => ({
          ...prev,
          headerMedia: {
            ...prev.headerMedia,
            mediaId,
            mediaUrl: "",
          },
        }));

        // Cache a data-URL preview in sessionStorage so the canvas node block
        // can display the image without making any API round-trip.
        // We read the file that is already in memory — no second network request.
        if (file.type.startsWith("image/")) {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              sessionStorage.setItem(
                `xbytechat:media-preview:${mediaId}`,
                reader.result
              );
            } catch {
              // sessionStorage quota exceeded — preview will fall back gracefully.
            }
          };
          reader.readAsDataURL(file);
        }
      }
    } catch (err) {
      console.error("❌ Header media upload failed", err);
    } finally {
      setMediaUploading(false);
    }
  };

  const handleHeaderMediaCaptionChange = caption => {
    setForm(prev => ({
      ...prev,
      headerMedia: { ...prev.headerMedia, caption },
    }));
  };

  const handleRemoveHeaderMedia = () => {
    setForm(prev => ({ ...prev, headerMedia: null }));
  };

  const replaceLegacyCondition = () => {
    const config = createConditionConfig();
    setBranchUnsupportedConfig(false);
    setForm(prev => ({ ...prev, ...config }));
  };

  const updateConditionRule = (index, patch) => {
    setForm(prev => ({
      ...prev,
      rules: (prev.rules || []).map((rule, ruleIndex) =>
        ruleIndex === index ? { ...rule, ...patch } : rule
      ),
    }));
  };

  const addConditionRule = () => setForm(prev => ({
    ...prev,
    rules: (prev.rules || []).length >= CONDITION_MAX_RULES
      ? prev.rules
      : [...(prev.rules || []), createConditionRule()],
  }));

  const removeConditionRule = index => setForm(prev => ({
    ...prev,
    rules: (prev.rules || []).filter((_, ruleIndex) => ruleIndex !== index),
  }));

  const moveConditionRule = (index, direction) => setForm(prev => {
    const rules = [...(prev.rules || [])];
    const target = index + direction;
    if (target < 0 || target >= rules.length) return prev;
    [rules[index], rules[target]] = [rules[target], rules[index]];
    return { ...prev, rules };
  });

  // 🆕 CTA Flow dropdown change
  const handleCtaFlowChange = e => {
    const value = e.target.value;
    const selected = ctaFlows.find(f => f.id === value);

    setForm(prev => ({
      ...prev,
      ctaFlowConfigId: value || "",
      ctaFlowName: selected?.flowName || "",
    }));
  };

  const handleTemplateHeaderMediaUpload = async e => {
    const file = e.target.files?.[0];
    if (!file) return;

    const mediaType = mediaTypeForHeaderKind(headerKind);
    if (!mediaType) return;

    const fd = new FormData();
    fd.append("file", file);
    fd.append("mediaType", mediaType);

    try {
      setMediaUploading(true);
      const { data } = await axiosClient.post("/auto-reply-builder/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const mediaId = data?.mediaId || data?.media_id || data?.id || "";
      if (!mediaId) return;

      setForm(prev => ({ ...prev, headerMediaUrl: `handle:${mediaId}` }));

      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            sessionStorage.setItem(
              `xbytechat:template-header-preview:${mediaId}`,
              reader.result
            );
          } catch {
            // Preview falls back to the stored media reference.
          }
        };
        reader.readAsDataURL(file);
      }
    } catch (err) {
      console.error("Template header media upload failed", err);
    } finally {
      setMediaUploading(false);
      e.target.value = "";
    }
  };

  const choiceValidation = useMemo(
    () => (isButtonChoiceNode ? validateChoiceConfig(form) : null),
    [form, isButtonChoiceNode]
  );
  const urlCtaValidation = useMemo(
    () => (isUrlCtaNode ? validateUrlCtaConfig(form) : null),
    [form, isUrlCtaNode]
  );
  const browseCatalogueValidation = useMemo(
    () =>
      isBrowseCatalogueNode
        ? validateBrowseCatalogueConfig(form)
        : null,
    [form, isBrowseCatalogueNode]
  );
  const searchCatalogueValidation = useMemo(
    () =>
      isSearchCatalogueNode
        ? validateSearchCatalogueConfig(
            form,
            flowNodes,
            flowEdges,
            node?.id
          )
        : null,
    [flowEdges, flowNodes, form, isSearchCatalogueNode, node?.id]
  );
  const presentCatalogueResultsValidation = useMemo(
    () =>
      isPresentCatalogueResultsNode
        ? validatePresentCatalogueResultsConfig(
            {
              ...form,
              commerce: form.commerceEnabled
                ? {
                    transactionType: form.commerceTransactionType,
                    quantityMode: form.commerceQuantityMode,
                    ...(form.commerceQuantityMode === "ask_customer"
                      ? { quantityPrompt: form.commerceQuantityPrompt }
                      : {}),
                  }
                : null,
            },
            flowNodes
          )
        : null,
    [form, flowNodes, isPresentCatalogueResultsNode]
  );
  const presentCatalogueResultsHasBlockingError =
    presentCatalogueResultsValidation &&
    !presentCatalogueResultsValidation.isValid &&
    presentCatalogueResultsValidation.reason !== "stale_search_node";
  const reviewAndSubmitValidation = useMemo(
    () =>
      isReviewAndSubmitNode
        ? validateReviewAndSubmitConfig(
            {
              promptText: form.promptText,
              detailBindings: form.reviewDetailBindings,
            },
            flowNodes,
            flowEdges,
            node?.id,
            { requireCommerceSource: false }
          )
        : null,
    [
      flowEdges,
      flowNodes,
      form.promptText,
      form.reviewDetailBindings,
      isReviewAndSubmitNode,
      node?.id,
    ]
  );
  const boundReviewSourceIds = new Set(
    (form.reviewDetailBindings || []).map(binding =>
      String(binding.sourceInputCaptureNodeId || "")
    )
  );
  const boundReviewFieldKeys = new Set(
    (form.reviewDetailBindings || []).map(binding =>
      String(binding.fieldKey || "").trim().toLowerCase()
    )
  );
  const unboundReviewFields = availableReviewFields.filter(
    field =>
      !boundReviewSourceIds.has(field.sourceInputCaptureNodeId) &&
      !boundReviewFieldKeys.has(field.fieldKey)
  );
  const addReviewBinding = sourceNodeId => {
    const field = availableReviewFields.find(
      option => option.sourceInputCaptureNodeId === sourceNodeId
    );
    if (!field) return;
    setForm(prev => {
      const bindings = Array.isArray(prev.reviewDetailBindings)
        ? prev.reviewDetailBindings
        : [];
      if (
        bindings.length >= REVIEW_AND_SUBMIT_MAXIMUM_BINDINGS ||
        bindings.some(
          binding =>
            String(binding.fieldKey || "").trim().toLowerCase() ===
            field.fieldKey
        )
      ) {
        return prev;
      }
      return {
        ...prev,
        reviewDetailBindings: [
          ...bindings,
          {
            sourceInputCaptureNodeId: field.sourceInputCaptureNodeId,
            fieldKey: field.fieldKey,
            labelOverride: "",
            sortOrder: bindings.length,
          },
        ],
      };
    });
  };
  const updateReviewBindings = updater =>
    setForm(prev => {
      const next = updater(
        Array.isArray(prev.reviewDetailBindings)
          ? prev.reviewDetailBindings
          : []
      );
      return {
        ...prev,
        reviewDetailBindings: next.map((binding, index) => ({
          ...binding,
          sortOrder: index,
        })),
      };
    });
  const moveReviewBinding = (index, offset) =>
    updateReviewBindings(bindings => {
      const target = index + offset;
      if (target < 0 || target >= bindings.length) return bindings;
      const next = [...bindings];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  const mediaUrlInvalid =
    isMediaNode &&
    !!String(form.mediaUrl || "").trim() &&
    !isValidHttpsUrl(form.mediaUrl);
  const mediaReferenceMissing =
    isMediaNode &&
    !String(form.mediaId || "").trim() &&
    !String(form.mediaUrl || "").trim();
  const standaloneMediaPreview =
    isMediaNode && form.mediaType === "image"
      ? String(form.mediaUrl || "").trim() ||
        (() => {
          try {
            return form.mediaId
              ? sessionStorage.getItem(`xbytechat:media-preview:${form.mediaId}`) || ""
              : "";
          } catch {
            return "";
          }
        })()
      : "";

  const handleSave = () => {
    if (!node) return;
    if (followUpValidationError) return;
    if (isLeadTemperatureNode && !["Cold", "Warm", "Hot"].includes(form.leadTemperature)) return;
    if (isUrlCtaNode && urlCtaValidation && !urlCtaValidation.isValid) return;
    if (isButtonChoiceNode && choiceValidation && !choiceValidation.isValid) return;
    if (isMediaNode && (mediaUrlInvalid || mediaReferenceMissing)) return;
    if (isTemplateNode && (templateHeaderMediaMissing || templateHeaderMediaInvalid)) return;
    const inputCaptureValidation = isInputCaptureNode
      ? validateInputCaptureConfig(form)
      : { isValid: true };
    if (!inputCaptureValidation.isValid) return;
    if (presentCatalogueResultsHasBlockingError) return;
    if (
      isReviewAndSubmitNode &&
      reviewAndSubmitValidation &&
      !reviewAndSubmitValidation.isValid
    ) {
      return;
    }

    // 🔢 Ensure `seconds` is always a number for wait nodes
    let configSeconds = form.seconds;
    if (isWaitNode) {
      const parsed = Number(form.seconds);
      if (!parsed || !Number.isFinite(parsed) || parsed <= 0) {
        configSeconds = 10; // fallback default
      } else {
        configSeconds = parsed;
      }
    }

    const normalizedChoiceOptions = (Array.isArray(form.options) ? form.options : []).map(
      (opt, index) => {
        const label = String(opt?.label || "").trim();
        const fallbackValue = String(index + 1);
        const value =
          typeof opt?.value === "string" && opt.value.trim()
            ? opt.value.trim()
            : fallbackValue;
        const description = String(opt?.description || "").trim();
        const userTerms = normalizeChoiceTerms(opt?.terms);

        const autoTerms = [];
        if (label) autoTerms.push(label);
        if (value && value !== fallbackValue) autoTerms.push(value);

        const terms = Array.from(new Set([...autoTerms, ...userTerms]));
        return { label, value, description, terms };
      }
    );

    let config = {
      ...form,
      ...(isWaitNode ? { seconds: configSeconds } : {}),
    };

    if (isLeadTemperatureNode) {
      config = { leadTemperature: form.leadTemperature, allowTemperatureDowngrade: form.allowTemperatureDowngrade === true };
    } else if (isUrlCtaNode) {
      config = normalizeUrlCtaConfig(form);
    } else if (isInputCaptureNode) {
      const numeric = ["integer", "decimal"].includes(form.inputType);
      config = {
        question: String(form.question || "").trim(),
        fieldKey: String(form.fieldKey || "").trim().toLowerCase(),
        inputType: String(form.inputType || "text").trim().toLowerCase(),
        invalidResponseMessage: String(form.invalidResponseMessage || "").trim(),
        maximumAttempts: Number(form.maximumAttempts),
        ...(numeric && form.minimum !== "" ? { minimum: Number(form.minimum) } : {}),
        ...(numeric && form.maximum !== "" ? { maximum: Number(form.maximum) } : {}),
        ...(form.inputType === "text" && form.minimumLength !== "" ? { minimumLength: Number(form.minimumLength) } : {}),
        ...(form.inputType === "text" && form.maximumLength !== "" ? { maximumLength: Number(form.maximumLength) } : {}),
        ...(String(form.requestContextFieldDefinitionId || "").trim()
          ? { requestContextFieldDefinitionId: String(form.requestContextFieldDefinitionId).trim() }
          : {}),
      };
    } else if (isMediaNode) {
      const mediaType = String(form.mediaType || "image").trim().toLowerCase();
      const mediaId = String(form.mediaId || "").trim();
      const mediaUrl = String(form.mediaUrl || "").trim();
      config = {
        mediaType,
        ...(mediaId ? { mediaId } : {}),
        ...(mediaUrl ? { mediaUrl } : {}),
        ...(mediaType !== "audio" && String(form.caption || "").trim()
          ? { caption: String(form.caption).trim() }
          : {}),
        ...(mediaType === "document" && String(form.filename || "").trim()
          ? { filename: String(form.filename).trim() }
          : {}),
      };
    } else if (isButtonChoiceNode) {
      config = {
        promptText: String(form.promptText || "").trim() || "Please choose an option:",
        options: normalizedChoiceOptions,
      };
      // Preserve headerMedia when set
      if (
        form.headerMedia &&
        form.headerMedia.kind &&
        (form.headerMedia.mediaId || form.headerMedia.mediaUrl)
      ) {
        config.headerMedia = {
          kind: form.headerMedia.kind,
          mediaId: form.headerMedia.mediaId || undefined,
          mediaUrl: form.headerMedia.mediaUrl || undefined,
          caption: form.headerMedia.caption || undefined,
        };
      }
    } else if (isDecisionTableNode) {
      config = normalizeDecisionTableConfig(form.decisionTable);
    } else if (isBranchNode) {
      if (branchUnsupportedConfig) {
        config = { ...(node.data?.config || {}) };
      } else {
        config = {
          fieldKey: String(form.fieldKey || "").trim().toLowerCase(),
          comparisonType: String(form.comparisonType || "string").trim().toLowerCase(),
          rules: (form.rules || []).map(rule => ({
            pathId: rule.pathId,
            label: String(rule.label || "").trim(),
            operator: rule.operator,
            comparisonValue: ["is_empty", "is_not_empty"].includes(rule.operator)
              ? null
              : String(rule.comparisonValue || "").trim(),
          })),
          defaultPathId: "default",
        };
      }
    } else if (isBrowseCatalogueNode) {
      config = {
        catalogId: String(form.catalogId || "").trim() || null,
        promptText:
          String(form.promptText ?? "").trim() ||
          BROWSE_CATALOGUE_DEFAULT_PROMPT,
        presentation:
          String(form.presentation || "auto").trim().toLowerCase() ||
          "auto",
      };
    } else if (isSearchCatalogueNode) {
      const searchMode = String(form.searchMode || "filtered")
        .trim()
        .toLowerCase();
      const categorySourceNodeId = String(
        form.categorySourceNodeId || ""
      ).trim();
      config = {
        searchMode,
        queryFieldKey:
          searchMode === "all" ? "" : String(form.queryFieldKey || "").trim(),
        catalogId: String(form.catalogId || "").trim() || null,
        maximumResults: Number(form.maximumResults),
        ...(searchMode !== "all" && categorySourceNodeId
          ? { categorySourceNodeId }
          : {}),
      };
    } else if (isPresentCatalogueResultsNode) {
      const commerce =
        form.commerceEnabled
          ? {
              transactionType: String(
                form.commerceTransactionType || ""
              ).trim(),
              quantityMode: String(form.commerceQuantityMode || "").trim(),
              ...(form.commerceQuantityMode === "ask_customer"
                ? {
                    quantityPrompt: String(
                      form.commerceQuantityPrompt || ""
                    ).trim(),
                  }
                : {}),
            }
          : null;
      config = {
        searchNodeId: String(form.searchNodeId || "").trim(),
        promptText:
          String(form.promptText ?? "").trim() ||
          PRESENT_CATALOGUE_RESULTS_DEFAULT_PROMPT,
        sectionMode: String(form.sectionMode || "flat").trim().toLowerCase(),
        ...(commerce ? { commerce } : {}),
      };
    } else if (isReviewAndSubmitNode) {
      const [decisionTableNodeId, fieldKey] = String(
        form.reviewDecisionPriceBinding || ""
      ).split("|");
      config = {
        promptText: String(form.promptText || "").trim(),
        detailBindings: (form.reviewDetailBindings || []).map(
          (binding, index) => ({
            sourceInputCaptureNodeId: String(
              binding.sourceInputCaptureNodeId || ""
            ).trim(),
            fieldKey: String(binding.fieldKey || "").trim().toLowerCase(),
            labelOverride:
              String(binding.labelOverride || "").trim() || null,
            sortOrder: index,
          })
        ),
        ...(form.reviewServicePricingSource === "DecisionTable"
          ? { servicePricing: {
                source: "DecisionTable",
                decisionTableNodeId,
                fieldKey,
              } }
          : {}),
      };
    } else if (isHandoverNode) {
      config = {
        ...(node.data?.config || {}),
        ackText: String(form.ackText || "").trim(),
      };
    } else if (isAddFlowNode) {
      config = {
        schemaVersion: 1,
        targetFlowId: String(form.targetFlowId || "").trim() || null,
      };
    }

    const updated = {
      ...node,
      data: {
        ...(node.data || {}),
        config,
        ...(followUpEligible
          ? {
              followUp,
              followUpEligible: true,
            }
          : {}),
        ...(isBranchNode && !branchUnsupportedConfig ? { legacyNodeType: null } : {}),
        ...(isAddFlowNode
          ? {
              addFlowTargetName:
                selectedAddFlowTarget?.isEligible !== false
                  ? selectedAddFlowTarget?.name || ""
                  : "",
              addFlowTargetUnavailable,
            }
          : {}),
      },
    };

    clearEditorDraft(getEditorDraftKey(node));
    onSave?.(updated);
    onClose?.();
  };

  const discardChangesAndClose = () => {
    if (node) {
      clearEditorDraft(getEditorDraftKey(node));
    }
    setSelectedTemplate(null);
    onClose?.();
  };

  const closeWithoutSave = () => {
    setSelectedTemplate(null);
    onClose?.();
  };

  if (!node) return null;
  if (isWhatsAppFlowNode) {
    return (
      <WhatsAppFlowNodeEditor
        node={node}
        onClose={onClose}
        onSave={onSave}
        templates={templates}
      />
    );
  }

  if (isDynamicWhatsFlowNode) {
    return (
      <WhatsAppFlowDynamicOptionsEditor
        node={node}
        onClose={onClose}
        onSave={onSave}
      />
    );
  }

  if (isSchedulingBookingNode) {
    return <SchedulingBookingEditor node={node} onClose={onClose} onSave={onSave} />;
  }
  if (isConfirmBookingNode) {
    return <ConfirmBookingEditor node={node} onClose={onClose} onSave={onSave} />;
  }
  if (isPrepareCommerceServiceNode) {
    return <PrepareCommerceServiceEditor node={node} onClose={onClose} onSave={onSave} />;
  }

  const choiceModeLabel =
    choiceValidation?.mode === "list" ? "WhatsApp list" : "reply buttons";

  const renderCharHelper = (currentLength, limit, errorMessage) => (
    <div className="flex items-center justify-between text-[11px]">
      <span className={errorMessage ? "text-red-600" : "text-slate-500"}>
        {errorMessage || " "}
      </span>
      <span className={currentLength > limit ? "text-red-600" : "text-slate-400"}>
        {currentLength}/{limit}
      </span>
    </div>
  );

  const title =
    node.type === "message"
      ? "Edit Message Node"
      : isInputCaptureNode
      ? "Edit Input Capture Node"
      : isButtonChoiceNode
      ? "Edit Choice Node"
      : isUrlCtaNode
      ? "Edit URL Node"
      : isBranchNode
      ? "Edit Condition Node"
      : isBrowseCatalogueNode
      ? "Edit Browse Catalogue Node"
      : isSearchCatalogueNode
      ? "Edit Search Catalogue Node"
      : isPresentCatalogueResultsNode
      ? "Edit Present Catalogue Results Node"
      : isReviewAndSubmitNode
      ? "Edit Review & Submit Node"
      : node.type === "template"
      ? "Edit Template Node"
      : isMediaNode
      ? "Edit Media Node"
      : isLeadTemperatureNode
      ? "Set Lead Temperature"
      : node.type === "tag"
      ? "Edit Tag Node"
      : node.type === "wait"
      ? "Edit Wait Node"
      : node.type === "cta_flow"
      ? "Edit CTA Flow Node"
      : node.type === "handover_agent"
      ? "Edit Agent Handover Node"
      : node.type === "add_flow"
      ? "Edit Add Flow Node"
      : node.type === "wait"
      ? "Edit Wait Node"
      : node.type === "cta_flow"
      ? "Edit CTA Flow Node"
      : "Edit Node";

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[1000] bg-black/10 backdrop-blur-[1px]"
        onClick={closeWithoutSave}
      />

      {/* Center modal */}
      <div className="fixed inset-0 z-[1001] overflow-y-auto px-4 pb-4 pt-[calc(var(--app-shell-topbar-size,64px)+16px)] sm:px-6 sm:pb-6 sm:pt-[calc(var(--app-shell-topbar-size,64px)+24px)]">
        <div className="flex min-h-full items-start justify-center">
        <div className="flex max-h-[calc(100vh-var(--app-shell-topbar-size,64px)-32px)] w-full max-w-3xl flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl sm:max-h-[calc(100vh-var(--app-shell-topbar-size,64px)-48px)]">

        {/* Pinned header */}
        <div className="flex items-start justify-between border-b border-slate-100 bg-gradient-to-r from-slate-50/80 to-white px-5 py-4">
          <div>
            <h2 className="text-[15px] font-bold tracking-tight text-slate-800">{title}</h2>
          </div>
          <button
            type="button"
            onClick={closeWithoutSave}
            className="ml-4 mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">

          {/* Message node fields */}
          {isMessageNode && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Reply text
              </label>
              <textarea
                name="text"
                value={form.text}
                onChange={handleChange}
                rows={4}
                className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="Type the auto-reply message..."
              />
              <p className="text-[11px] leading-relaxed text-slate-500">
                Insert an AutoReply session value with{" "}
                <code className="rounded bg-slate-100 px-1 py-0.5 text-slate-700">
                  {"{{quote.amount}}"}
                </code>
                . Prefix the opening braces with a backslash to send a placeholder literally.
              </p>
            </div>
          )}

          {isInputCaptureNode && (
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Question</label>
                <textarea
                  name="question"
                  value={form.question}
                  onChange={handleChange}
                  rows={3}
                  maxLength={1024}
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Field key</label>
                  <input name="fieldKey" value={form.fieldKey} onChange={handleChange} maxLength={128}
                    className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    placeholder="customer.email" />
                  <p className="text-[11px] text-slate-500">Lowercase dot-separated identifier.</p>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Input type</label>
                  <select value={form.inputType} onChange={e => {
                    const inputType = e.target.value;
                    setForm(prev => ({ ...prev, inputType, invalidResponseMessage: defaultInvalidResponse(inputType), minimum: "", maximum: "", minimumLength: "", maximumLength: "" }));
                  }} className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500">
                    {INPUT_CAPTURE_TYPES.map(type => <option key={type.value} value={type.value}>{type.label}</option>)}
                  </select>
                  <p className="text-[11px] text-slate-500">{INPUT_CAPTURE_TYPES.find(type => type.value === form.inputType)?.help}</p>
                </div>
              </div>

              {["integer", "decimal"].includes(form.inputType) && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="space-y-2 text-sm font-medium text-gray-700">Minimum
                    <input type="number" name="minimum" value={form.minimum} onChange={handleChange} step={form.inputType === "integer" ? 1 : "any"}
                      className="block w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
                  </label>
                  <label className="space-y-2 text-sm font-medium text-gray-700">Maximum
                    <input type="number" name="maximum" value={form.maximum} onChange={handleChange} step={form.inputType === "integer" ? 1 : "any"}
                      className="block w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
                  </label>
                </div>
              )}

              {form.inputType === "text" && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="space-y-2 text-sm font-medium text-gray-700">Minimum length
                    <input type="number" min="1" max="2000" name="minimumLength" value={form.minimumLength} onChange={handleChange}
                      className="block w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
                  </label>
                  <label className="space-y-2 text-sm font-medium text-gray-700">Maximum length
                    <input type="number" min="1" max="2000" name="maximumLength" value={form.maximumLength} onChange={handleChange}
                      className="block w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
                  </label>
                </div>
              )}

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Invalid response guidance</label>
                <textarea name="invalidResponseMessage" value={form.invalidResponseMessage} onChange={handleChange} rows={2} maxLength={512}
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500" />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Maximum attempts</label>
                <input type="number" min="1" max="5" name="maximumAttempts" value={form.maximumAttempts} onChange={handleChange}
                  className="w-32 rounded-md border border-slate-200 px-3 py-2 text-sm" />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Also store in Request Context</label>
                <select
                  aria-label="Request Context field"
                  value={form.requestContextFieldDefinitionId || ""}
                  onChange={event => setForm(previous => ({ ...previous, requestContextFieldDefinitionId: event.target.value }))}
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                >
                  <option value="">Do not store in Request Context</option>
                  {requestContextFields
                    .filter(field => isInputCaptureRequestContextCompatible(form.inputType, field.dataType))
                    .map(field => <option key={field.id} value={field.id}>{field.label} ({field.dataType})</option>)}
                </select>
                <p className="text-[11px] text-slate-500">Optional. The session field remains available to the current automation.</p>
                {requestContextFieldsError && <p role="alert" className="text-[11px] text-amber-700">{requestContextFieldsError}</p>}
              </div>

              {!validateInputCaptureConfig(form).isValid && (
                <p className="text-[11px] font-medium text-red-600">{validateInputCaptureConfig(form).message}</p>
              )}
            </div>
          )}

          {isMediaNode && (
            <div className="space-y-4">
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] leading-relaxed text-amber-800">
                Free-form media is intended for the WhatsApp customer service window. Use an approved template when WhatsApp requires one.
              </div>

              <div className="space-y-2">
                <label htmlFor="auto-reply-media-type" className="block text-sm font-medium text-gray-700">
                  Media type
                </label>
                <select
                  id="auto-reply-media-type"
                  name="mediaType"
                  value={form.mediaType}
                  onChange={e => {
                    const mediaType = e.target.value;
                    setForm(prev => ({
                      ...prev,
                      mediaType,
                      mediaId: "",
                      mediaUrl: "",
                      caption: mediaType === "audio" ? "" : prev.caption,
                      filename: mediaType === "document" ? prev.filename : "",
                    }));
                  }}
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                >
                  <option value="image">Image</option>
                  <option value="video">Video</option>
                  <option value="document">Document/PDF</option>
                  <option value="audio">Audio</option>
                </select>
              </div>

              <div className="space-y-2">
                <label htmlFor="auto-reply-media-upload" className="block text-sm font-medium text-gray-700">
                  Upload media
                </label>
                <div className="flex flex-wrap items-center gap-2">
                  <label className="flex cursor-pointer items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-100">
                    <Upload className="h-3.5 w-3.5" />
                    {mediaUploading ? "Uploading..." : "Upload file"}
                    <input
                      id="auto-reply-media-upload"
                      type="file"
                      className="hidden"
                      accept={
                        form.mediaType === "image"
                          ? "image/*"
                          : form.mediaType === "video"
                          ? "video/*"
                          : form.mediaType === "audio"
                          ? "audio/*"
                          : ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,text/plain,application/*"
                      }
                      onChange={handleStandaloneMediaUpload}
                      disabled={mediaUploading}
                    />
                  </label>
                  {form.mediaId ? (
                    <span className="flex items-center gap-1 rounded bg-emerald-100 px-2 py-1 text-xs text-emerald-800">
                      Uploaded
                      <button
                        type="button"
                        onClick={() => setForm(prev => ({ ...prev, mediaId: "" }))}
                        className="ml-1 text-red-500 hover:text-red-700"
                        title="Remove uploaded media"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </span>
                  ) : null}
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="auto-reply-media-url" className="block text-sm font-medium text-gray-700">
                  HTTPS media URL <span className="font-normal text-slate-400">(optional alternative)</span>
                </label>
                <input
                  id="auto-reply-media-url"
                  type="url"
                  value={form.mediaUrl}
                  onChange={e =>
                    setForm(prev => ({
                      ...prev,
                      mediaUrl: e.target.value,
                      mediaId: e.target.value ? "" : prev.mediaId,
                    }))
                  }
                  className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-1 ${
                    mediaUrlInvalid
                      ? "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500"
                      : "border-slate-200 bg-white focus:border-emerald-500 focus:ring-emerald-500"
                  }`}
                  placeholder="https://example.com/media-file"
                />
                {mediaUrlInvalid ? (
                  <p className="text-[11px] text-red-600">Media URL must be a valid HTTPS URL.</p>
                ) : null}
                {mediaReferenceMissing ? (
                  <p className="text-[11px] text-red-600">Upload a file or provide an HTTPS media URL.</p>
                ) : null}
              </div>

              {standaloneMediaPreview ? (
                <img
                  src={standaloneMediaPreview}
                  alt={form.caption || "Media preview"}
                  className="max-h-52 w-full rounded-lg border border-slate-200 object-contain"
                />
              ) : null}

              {form.mediaType !== "audio" ? (
                <div className="space-y-2">
                  <label htmlFor="auto-reply-media-caption" className="block text-sm font-medium text-gray-700">
                    Caption <span className="font-normal text-slate-400">(optional)</span>
                  </label>
                  <textarea
                    id="auto-reply-media-caption"
                    value={form.caption}
                    onChange={e => setForm(prev => ({ ...prev, caption: e.target.value }))}
                    rows={3}
                    className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    placeholder="Add an optional caption..."
                  />
                </div>
              ) : null}

              {form.mediaType === "document" ? (
                <div className="space-y-2">
                  <label htmlFor="auto-reply-media-filename" className="block text-sm font-medium text-gray-700">
                    Filename <span className="font-normal text-slate-400">(optional)</span>
                  </label>
                  <input
                    id="auto-reply-media-filename"
                    type="text"
                    value={form.filename}
                    onChange={e => setForm(prev => ({ ...prev, filename: e.target.value }))}
                    className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    placeholder="document.pdf"
                  />
                </div>
              ) : null}
            </div>
          )}

          {isButtonChoiceNode && (
            <div className="space-y-3">
              <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">
                      Current send mode: {choiceModeLabel}
                    </p>
                    <p className="text-[11px] text-slate-500">
                      {choiceValidation?.mode === "list"
                        ? "4-10 buttons send as a WhatsApp list. Each list title allows 24 characters and description allows 72."
                        : "1-3 buttons send as reply buttons. Each button title allows 20 characters."}
                    </p>
                  </div>
                  <span className="rounded-md bg-white px-2 py-1 text-[11px] font-medium text-slate-600">
                    {choiceValidation?.optionCount || 0}/{WHATSAPP_INTERACTIVE_OPTION_MAX} buttons
                  </span>
                </div>
                {choiceValidation?.globalErrors?.length > 0 && (
                  <div className="mt-3 space-y-1">
                    {choiceValidation.globalErrors.map(error => (
                      <p key={error} className="text-[11px] text-red-600">
                        {error}
                      </p>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Prompt Text
                </label>
                <textarea
                  name="promptText"
                  value={form.promptText}
                  onChange={e =>
                    setForm(prev => ({ ...prev, promptText: e.target.value }))
                  }
                  rows={4}
                  className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                    choiceValidation?.promptError
                      ? "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500"
                      : "border-slate-200 bg-white focus:border-emerald-500 focus:ring-emerald-500"
                  }`}
                  placeholder="Type the choice prompt..."
                />
                {renderCharHelper(
                  String(form.promptText || "").trim().length,
                  choiceValidation?.promptLimit || 1024,
                  choiceValidation?.promptError
                )}
              </div>

              {/* ─── Header Media (optional) ─── */}
              <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  Header Media <span className="font-normal normal-case text-slate-400">(optional)</span>
                </label>

                <select
                  value={form.headerMedia?.kind || "none"}
                  onChange={e => handleHeaderMediaKindChange(e.target.value)}
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                >
                  <option value="none">None</option>
                  <option value="image">Image</option>
                  <option value="video">Video</option>
                  <option value="document">Document</option>
                </select>

                {form.headerMedia?.kind && form.headerMedia.kind !== "none" && (
                  <div className="space-y-2">
                    {/* Upload */}
                    <div className="flex items-center gap-2">
                      <label className="flex cursor-pointer items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-100">
                        <Upload className="h-3.5 w-3.5" />
                        {mediaUploading ? "Uploading…" : "Upload file"}
                        <input
                          type="file"
                          className="hidden"
                          accept={
                            form.headerMedia.kind === "image"
                              ? "image/*"
                              : form.headerMedia.kind === "video"
                              ? "video/*"
                              : "*/*"
                          }
                          onChange={handleHeaderMediaUpload}
                          disabled={mediaUploading}
                        />
                      </label>

                      {form.headerMedia.mediaId && (
                        <span className="flex items-center gap-1 rounded bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800">
                          ✓ Uploaded
                          <button
                            type="button"
                            onClick={handleRemoveHeaderMedia}
                            className="ml-1 text-red-500 hover:text-red-700"
                            title="Remove media"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </span>
                      )}
                    </div>

                    {/* Caption */}
                    <input
                      type="text"
                      value={form.headerMedia.caption || ""}
                      onChange={e => handleHeaderMediaCaptionChange(e.target.value)}
                      className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                      placeholder="Caption (used in list fallback)"
                    />

                    <p className="text-[10px] text-slate-400">
                      ≤3 buttons → media shows as button header. &gt;3 buttons → media sent first, then list.
                    </p>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between pb-1">
                  <label className="block text-sm font-semibold text-slate-700">
                    Buttons
                  </label>
                  <button
                    type="button"
                    onClick={handleAddChoiceOption}
                    disabled={(choiceValidation?.optionCount || 0) >= WHATSAPP_INTERACTIVE_OPTION_MAX}
                    className="flex items-center gap-1 rounded-md bg-emerald-50 px-2.5 py-1.5 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-100"
                  >
                    + Add button
                  </button>
                </div>

                {(Array.isArray(form.options) ? form.options : []).map((opt, index) => {
                  const optionErrors = choiceValidation?.optionErrors?.[index] || {};
                  const labelLength = String(opt?.label || "").trim().length;
                  const valueLength = String(opt?.value || "").trim().length;
                  const descriptionLength = String(opt?.description || "").trim().length;

                  return (
                    <div key={index} className="space-y-2 rounded-md border border-slate-200 p-2">
                      <div className="flex items-start gap-2">
                        <div className="flex-1 space-y-1">
                          <input
                            type="text"
                            value={opt?.label || ""}
                            onChange={e =>
                              handleChoiceOptionChange(index, "label", e.target.value)
                            }
                            className={`w-full rounded-md border px-3 py-2 text-sm transition-colors focus:bg-white focus:outline-none focus:ring-1 ${
                              optionErrors.label
                                ? "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500"
                                : "border-slate-200 bg-slate-50 focus:border-emerald-500 focus:ring-emerald-500"
                            }`}
                            placeholder={
                              choiceValidation?.mode === "list"
                                ? `List title ${index + 1}`
                                : `Button title ${index + 1}`
                            }
                          />
                          {renderCharHelper(
                            labelLength,
                            choiceValidation?.titleLimit || 20,
                            optionErrors.label
                          )}
                        </div>
                        <div className="flex-1 space-y-1">
                          <input
                            type="text"
                            value={opt?.value || ""}
                            onChange={e =>
                              handleChoiceOptionChange(index, "value", e.target.value)
                            }
                            className={`w-full rounded-md border px-3 py-2 text-sm transition-colors focus:bg-white focus:outline-none focus:ring-1 ${
                              optionErrors.value
                                ? "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500"
                                : "border-slate-200 bg-slate-50 focus:border-emerald-500 focus:ring-emerald-500"
                            }`}
                            placeholder={`Value ${index + 1}`}
                          />
                          {renderCharHelper(
                            valueLength,
                            choiceValidation?.valueLimit || 200,
                            optionErrors.value
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleDeleteChoiceOption(index)}
                          className="flex h-9 w-9 items-center justify-center rounded-md border border-red-100 text-red-500 transition-colors hover:bg-red-50 hover:text-red-700"
                          title="Delete button"
                        >
                          ✕
                        </button>
                      </div>
                      {choiceValidation?.mode === "list" && (
                        <div className="space-y-1">
                          <label className="block text-xs text-gray-600">
                            Description (optional)
                          </label>
                          <input
                            type="text"
                            value={opt?.description || ""}
                            onChange={e =>
                              handleChoiceOptionChange(index, "description", e.target.value)
                            }
                            className={`w-full rounded-md border px-3 py-2 text-sm transition-colors focus:bg-white focus:outline-none focus:ring-1 ${
                              optionErrors.description
                                ? "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500"
                                : "border-slate-200 bg-slate-50 focus:border-emerald-500 focus:ring-emerald-500"
                            }`}
                            placeholder="Shown under the list title in WhatsApp"
                          />
                          {renderCharHelper(
                            descriptionLength,
                            WHATSAPP_LIST_ROW_DESCRIPTION_LIMIT,
                            optionErrors.description
                          )}
                        </div>
                      )}
                      <div className="space-y-1">
                        <label className="block text-xs text-gray-600">
                          Words (comma separated)
                        </label>
                        <input
                          type="text"
                          value={normalizeChoiceTerms(opt?.terms).join(", ")}
                          onChange={e =>
                            handleChoiceOptionChange(index, "terms", e.target.value)
                          }
                          className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          placeholder="pricing, cost, plan"
                        />
                      </div>
                    </div>
                  );
                })}
                {choiceValidation?.mode === "list" && (
                  <div className="flex justify-end pt-1">
                    <button
                      type="button"
                      onClick={handleAddChoiceOption}
                      disabled={(choiceValidation?.optionCount || 0) >= WHATSAPP_INTERACTIVE_OPTION_MAX}
                      className="flex items-center gap-1 rounded-md bg-emerald-50 px-2.5 py-1.5 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-100"
                    >
                      + Add button
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {isDecisionTableNode && (
            <DecisionTableEditor
              value={form.decisionTable}
              onChange={decisionTable =>
                setForm(previous => ({ ...previous, decisionTable }))
              }
            />
          )}

          {isBranchNode && (
            <div className="space-y-3">
              {branchUnsupportedConfig && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                  <p className="text-xs text-amber-800">Legacy Condition configuration is retained but cannot execute.</p>
                  <button type="button" onClick={replaceLegacyCondition} className="mt-2 rounded-md border border-amber-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-amber-800">
                    Replace legacy configuration
                  </button>
                </div>
              )}

              {!branchUnsupportedConfig && <>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Session Field Key</label>
                  <select
                    aria-label="Session Field Key"
                    value={conditionFieldSelection}
                    onChange={e => selectConditionField(e.target.value)}
                    className="w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500"
                  >
                    {availableConditionFields.map(field => (
                      <option key={field.fieldKey} value={field.fieldKey}>
                        {`${field.fieldKey} · ${field.inputTypes.join("/")} · ${field.question || "Input Capture"}`}
                      </option>
                    ))}
                    <option value={CUSTOM_CONDITION_FIELD}>Custom field key</option>
                  </select>
                  {conditionFieldSelection === CUSTOM_CONDITION_FIELD && (
                    <input
                      aria-label="Custom Session Field Key"
                      value={form.fieldKey}
                      onChange={e => setForm(prev => ({ ...prev, fieldKey: e.target.value }))}
                      className="mt-2 w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500"
                      placeholder="transaction.quantity"
                    />
                  )}
                  {!selectedConditionField && normalizedConditionFieldKey && (
                    <p role="status" className="mt-1 text-xs text-amber-700">
                      This field is not captured by any Input Capture node in this flow.
                    </p>
                  )}
                  {selectedConditionField?.hasTypeConflict && (
                    <p role="status" className="mt-1 text-xs text-amber-700">
                      This field is captured with multiple input types in this flow. Align the Input Capture nodes before relying on this Condition.
                    </p>
                  )}
                  {conditionFieldTypeMismatch && !selectedConditionField?.hasTypeConflict && (
                    <p role="status" className="mt-1 text-xs text-amber-700">
                      This field is captured as {selectedConditionField.inputType}, but this Condition compares it as {form.comparisonType}.
                    </p>
                  )}
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Comparison Type</label>
                  <select aria-label="Comparison Type" value={form.comparisonType} onChange={e => {
                    updateConditionComparisonType(e.target.value);
                  }} className="w-full rounded-md border px-3 py-2 text-sm">
                    {CONDITION_TYPES.map(type => <option key={type} value={type}>{type.replace("_", " ")}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700">Ordered rules</label>
                    <button type="button" aria-label="Add Condition rule" onClick={addConditionRule} disabled={(form.rules || []).length >= CONDITION_MAX_RULES} className="inline-flex items-center gap-1 rounded-md border border-emerald-200 px-2 py-1 text-xs text-emerald-700 disabled:opacity-50"><Plus size={14} /> Add rule</button>
                  </div>
                  {(form.rules || []).map((rule, index) => {
                    const noValue = ["is_empty", "is_not_empty"].includes(rule.operator);
                    return <div key={rule.pathId} className="space-y-2 rounded-md border border-slate-200 p-2">
                      <div className="flex items-center gap-1">
                        <input aria-label={`Condition rule ${index + 1} label`} value={rule.label} onChange={e => updateConditionRule(index, { label: e.target.value })} className="min-w-0 flex-1 rounded-md border px-2 py-1.5 text-sm" placeholder="Path label" />
                        <button type="button" aria-label={`Move rule ${index + 1} up`} disabled={index === 0} onClick={() => moveConditionRule(index, -1)} className="p-1 disabled:opacity-30"><ArrowUp size={15} /></button>
                        <button type="button" aria-label={`Move rule ${index + 1} down`} disabled={index === (form.rules || []).length - 1} onClick={() => moveConditionRule(index, 1)} className="p-1 disabled:opacity-30"><ArrowDown size={15} /></button>
                        <button type="button" aria-label={`Remove rule ${index + 1}`} onClick={() => removeConditionRule(index)} className="p-1 text-red-600"><Trash2 size={15} /></button>
                      </div>
                      <select aria-label={`Condition rule ${index + 1} operator`} value={rule.operator} onChange={e => updateConditionRule(index, { operator: e.target.value, comparisonValue: ["is_empty", "is_not_empty"].includes(e.target.value) ? "" : rule.comparisonValue })} className="w-full rounded-md border px-2 py-1.5 text-sm">
                        {operatorsForConditionType(form.comparisonType).map(op => <option key={op} value={op}>{op.replaceAll("_", " ")}</option>)}
                      </select>
                      {!noValue && <input aria-label={`Condition rule ${index + 1} value`} value={rule.comparisonValue || ""} onChange={e => updateConditionRule(index, { comparisonValue: e.target.value })} className="w-full rounded-md border px-2 py-1.5 text-sm" placeholder="Comparison value" />}
                      <p className="truncate text-[10px] text-slate-400">{rule.pathId}</p>
                    </div>;
                  })}
                  <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-xs font-medium text-slate-600">Default path</div>
                </div>
              </>}
            </div>
          )}

          {isAddFlowNode && (
            <div className="space-y-3">
              <div className="space-y-2">
                <label
                  htmlFor="add-flow-target"
                  className="block text-sm font-medium text-gray-700"
                >
                  Flow to add
                </label>
                <select
                  id="add-flow-target"
                  value={form.targetFlowId || ""}
                  onChange={event =>
                    setForm(prev => ({
                      ...prev,
                      targetFlowId: event.target.value || null,
                    }))
                  }
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                >
                  <option value="">Select an existing flow</option>
                  {addFlowTargetUnavailable && (
                    <option value={form.targetFlowId}>
                      Selected flow unavailable
                    </option>
                  )}
                  {eligibleAddFlowOptions.map(flow => (
                    <option key={flow.id} value={flow.id}>
                      {flow.name}
                    </option>
                  ))}
                </select>
                {addFlowTargetUnavailable && (
                  <p className="text-xs font-medium text-amber-700">
                    Selected flow unavailable. Choose another active flow to replace it.
                  </p>
                )}
              </div>

              <p className="text-xs leading-5 text-slate-500">
                When the customer reaches this step, XploreByte will end the current automation journey and start the selected flow immediately.
              </p>
            </div>
          )}

          {isUrlCtaNode && (
            <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_280px]">
              <div className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="url-cta-body" className="block text-sm font-medium text-gray-700">
                    Message text
                  </label>
                  <textarea
                    id="url-cta-body"
                    name="body"
                    value={form.body}
                    onChange={handleChange}
                    rows={4}
                    className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-1 ${
                      urlCtaValidation?.errors?.body
                        ? "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500"
                        : "border-slate-200 focus:border-emerald-500 focus:ring-emerald-500"
                    }`}
                    placeholder="View the complete programme."
                  />
                  {urlCtaValidation?.errors?.body ? (
                    <p className="text-[11px] text-red-600">{urlCtaValidation.errors.body}</p>
                  ) : null}
                </div>

                <div className="space-y-2">
                  <label htmlFor="url-cta-label" className="block text-sm font-medium text-gray-700">
                    Button label
                  </label>
                  <input
                    id="url-cta-label"
                    name="buttonLabel"
                    value={form.buttonLabel}
                    onChange={handleChange}
                    className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-1 ${
                      urlCtaValidation?.errors?.buttonLabel
                        ? "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500"
                        : "border-slate-200 focus:border-emerald-500 focus:ring-emerald-500"
                    }`}
                    placeholder="Visit Website"
                  />
                  {urlCtaValidation?.errors?.buttonLabel ? (
                    <p className="text-[11px] text-red-600">{urlCtaValidation.errors.buttonLabel}</p>
                  ) : null}
                </div>

                <div className="space-y-2">
                  <label htmlFor="url-cta-url" className="block text-sm font-medium text-gray-700">
                    Website URL
                  </label>
                  <input
                    id="url-cta-url"
                    name="url"
                    type="url"
                    value={form.url}
                    onChange={handleChange}
                    className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-1 ${
                      urlCtaValidation?.errors?.url
                        ? "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500"
                        : "border-slate-200 focus:border-emerald-500 focus:ring-emerald-500"
                    }`}
                    placeholder="https://example.com/"
                  />
                  <p className="text-[11px] text-slate-500">URL must begin with https://</p>
                  {urlCtaValidation?.errors?.url ? (
                    <p className="text-[11px] text-red-600">{urlCtaValidation.errors.url}</p>
                  ) : null}
                </div>

                <div className="space-y-2">
                  <label htmlFor="url-cta-after-send" className="block text-sm font-medium text-gray-700">
                    After sending
                  </label>
                  <select
                    id="url-cta-after-send"
                    name="afterSend"
                    value={form.afterSend}
                    onChange={handleChange}
                    className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  >
                    <option value="continue">Continue to next node</option>
                    <option value="end">End this path</option>
                  </select>
                  {urlCtaValidation?.errors?.afterSend ? (
                    <p className="text-[11px] text-red-600">{urlCtaValidation.errors.afterSend}</p>
                  ) : null}
                  <p className="text-[11px] text-slate-500">The node does not wait for the customer to click.</p>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-[#efeae2] p-3">
                <p className="mb-2 text-[10px] font-bold uppercase tracking-wide text-slate-500">WhatsApp preview</p>
                <div className="overflow-hidden rounded-lg bg-white shadow-sm">
                  <div className="whitespace-pre-wrap px-3 py-3 text-[12px] text-slate-700">
                    {String(form.body || "").trim() || "Your message text"}
                  </div>
                  <div className="border-t border-slate-100 px-3 py-2 text-center text-[12px] font-semibold text-sky-600">
                    {String(form.buttonLabel || "").trim() || "Visit Website"}
                  </div>
                </div>
                {getUrlCtaHostname(form.url) ? (
                  <p className="mt-2 truncate text-center text-[10px] text-slate-500">
                    {getUrlCtaHostname(form.url)}
                  </p>
                ) : null}
                <p className="mt-3 text-[11px] leading-relaxed text-slate-600">
                  Clicking the button opens the website.
                </p>
              </div>
            </div>
          )}

          {isBrowseCatalogueNode && (
            <div className="space-y-4">
              <div className="rounded-md border border-cyan-100 bg-cyan-50/50 px-3 py-2 text-xs text-slate-600">
                Let customers choose a catalogue category.
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Catalogue
                </label>
                <select
                  aria-label="Browse Catalogue Catalogue"
                  value={String(form.catalogId || "")}
                  onChange={event =>
                    setForm(prev => ({ ...prev, catalogId: event.target.value }))
                  }
                  className="w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">Business Default Catalogue</option>
                  {form.catalogId &&
                    !catalogOptions.some(item => item?.id === form.catalogId) && (
                      <option value={form.catalogId} disabled>
                        Previously selected catalogue
                      </option>
                    )}
                  {catalogOptions.map(catalog => (
                    <option key={catalog.id} value={catalog.id}>
                      {catalog.name}
                    </option>
                  ))}
                </select>
                {catalogOptionsLoading && (
                  <p className="mt-1 text-xs text-slate-500">Loading active catalogues…</p>
                )}
                {catalogOptionsError && (
                  <p role="status" className="mt-1 text-xs text-amber-700">
                    {catalogOptionsError}
                  </p>
                )}
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Prompt
                </label>
                <textarea
                  aria-label="Browse Catalogue Prompt"
                  rows={3}
                  maxLength={BROWSE_CATALOGUE_MAXIMUM_PROMPT_LENGTH + 1}
                  value={form.promptText}
                  onChange={event =>
                    setForm(prev => ({ ...prev, promptText: event.target.value }))
                  }
                  className="w-full resize-none rounded-md border px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500"
                />
                {renderCharHelper(
                  String(form.promptText || "").length,
                  BROWSE_CATALOGUE_MAXIMUM_PROMPT_LENGTH,
                  browseCatalogueValidation?.reason === "prompt_too_long"
                    ? browseCatalogueValidation.message
                    : ""
                )}
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Presentation
                </label>
                <select
                  aria-label="Browse Catalogue Presentation"
                  value={String(form.presentation || "auto")}
                  onChange={event =>
                    setForm(prev => ({ ...prev, presentation: event.target.value }))
                  }
                  className="w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500"
                >
                  {BROWSE_CATALOGUE_PRESENTATIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                <p className="text-xs font-semibold text-slate-700">Paths</p>
                <p className="mt-1 text-xs text-slate-500">
                  Selected · No Categories
                </p>
              </div>
            </div>
          )}

          {isSearchCatalogueNode && (
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Search mode
                </label>
                <select
                  aria-label="Search Catalogue Search Mode"
                  value={String(form.searchMode || "filtered")}
                  onChange={event =>
                    setForm(prev => ({
                      ...prev,
                      searchMode: event.target.value,
                      ...(event.target.value === "all"
                        ? { queryFieldKey: "", categorySourceNodeId: "" }
                        : {}),
                    }))
                  }
                  className="w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500"
                >
                  {SEARCH_CATALOGUE_MODES.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {String(form.searchMode || "filtered") !== "all" && (
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Query field
                </label>
                <select
                  aria-label="Search Catalogue Query Field"
                  value={selectedSearchField?.fieldKey || normalizedSearchFieldKey}
                  onChange={event =>
                    setForm(prev => ({ ...prev, queryFieldKey: event.target.value }))
                  }
                  className="w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">No query field</option>
                  {!selectedSearchField && normalizedSearchFieldKey && (
                    <option value={normalizedSearchFieldKey}>
                      {normalizedSearchFieldKey}
                    </option>
                  )}
                  {availableSearchFields.map(field => (
                    <option key={field.fieldKey} value={field.fieldKey}>
                      {`${field.question || "Input Capture"} · ${field.fieldKey} · ${field.inputTypes.join("/")}`}
                    </option>
                  ))}
                </select>
                {!selectedSearchField && normalizedSearchFieldKey && (
                  <p role="status" className="mt-1 text-xs text-amber-700">
                    This field is not available as compatible text Input Capture in this flow.
                  </p>
                )}
                <p className="mt-1 text-xs text-slate-500">
                  Optional when a Browse Catalogue source is selected.
                </p>
              </div>
              )}

              {String(form.searchMode || "filtered") !== "all" && (
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Category source
                </label>
                <select
                  aria-label="Search Catalogue Category Source"
                  value={String(form.categorySourceNodeId || "")}
                  onChange={event =>
                    setForm(prev => ({
                      ...prev,
                      categorySourceNodeId: event.target.value,
                    }))
                  }
                  className="w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">None</option>
                  {form.categorySourceNodeId && !selectedBrowseSource && (
                    <option value={form.categorySourceNodeId} disabled>
                      Previously selected Browse Catalogue node
                    </option>
                  )}
                  {availableBrowseSources.map(source => (
                    <option key={source.id} value={source.id}>
                      {source.label || "Browse Catalogue"}
                    </option>
                  ))}
                </select>
                {form.categorySourceNodeId && !selectedBrowseSource && (
                  <p role="status" className="mt-1 text-xs text-amber-700">
                    {searchCatalogueValidation?.message ||
                      "The selected Browse Catalogue source is not valid upstream."}
                  </p>
                )}
                <p className="mt-1 text-xs text-slate-500">
                  Uses the category selected by an earlier Browse Catalogue node.
                </p>
              </div>
              )}

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Catalogue
                </label>
                <select
                  aria-label="Search Catalogue Catalogue"
                  value={String(form.catalogId || "")}
                  onChange={event =>
                    setForm(prev => ({ ...prev, catalogId: event.target.value }))
                  }
                  className="w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">
                    {String(form.searchMode || "filtered") === "all"
                      ? "Select a catalogue"
                      : "Business Default Catalogue"}
                  </option>
                  {form.catalogId &&
                    !catalogOptions.some(item => item?.id === form.catalogId) && (
                      <option value={form.catalogId} disabled>
                        Previously selected catalogue
                      </option>
                    )}
                  {catalogOptions.map(catalog => (
                    <option key={catalog.id} value={catalog.id}>
                      {catalog.name}
                    </option>
                  ))}
                </select>
                {catalogOptionsLoading && (
                  <p className="mt-1 text-xs text-slate-500">Loading active catalogues…</p>
                )}
                {catalogOptionsError && (
                  <p role="status" className="mt-1 text-xs text-amber-700">
                    {catalogOptionsError}
                  </p>
                )}
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Maximum results
                </label>
                <input
                  aria-label="Search Catalogue Maximum Results"
                  type="number"
                  min={SEARCH_CATALOGUE_MINIMUM_RESULTS}
                  max={SEARCH_CATALOGUE_MAXIMUM_RESULTS}
                  step="1"
                  value={form.maximumResults}
                  onChange={event =>
                    setForm(prev => ({ ...prev, maximumResults: event.target.value }))
                  }
                  className="w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500"
                />
                <p className="mt-1 text-xs text-slate-500">
                  Show between 2 and 30 deterministic matches per result window.
                </p>
                {searchCatalogueValidation &&
                  !searchCatalogueValidation.isValid &&
                  ![
                    "stale_category_source",
                    "wrong_category_source_type",
                    "category_source_not_upstream",
                    "category_source_self_reference",
                  ].includes(searchCatalogueValidation.reason) && (
                  <p role="alert" className="mt-1 text-xs text-red-600">
                    {searchCatalogueValidation.message}
                  </p>
                )}
              </div>

              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                <p className="text-xs font-semibold text-slate-700">Paths</p>
                <p className="mt-1 text-xs text-slate-500">
                  No Results · Single Result · Multiple Results
                </p>
              </div>
            </div>
          )}

          {isPresentCatalogueResultsNode && (
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Search results source
                </label>
                <select
                  aria-label="Present Catalogue Results Search Source"
                  value={String(form.searchNodeId || "")}
                  onChange={event =>
                    setForm(prev => ({
                      ...prev,
                      searchNodeId: event.target.value,
                    }))
                  }
                  className="w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500"
                >
                  {!form.searchNodeId && (
                    <option value="">Select a Search Catalogue node</option>
                  )}
                  {form.searchNodeId && !selectedSearchNode && (
                    <option value={form.searchNodeId}>
                      Previously selected Search Catalogue node
                    </option>
                  )}
                  {availableSearchNodes.map(searchNode => (
                    <option key={searchNode.id} value={searchNode.id}>
                      {`${searchNode.label || "Search Catalogue"} · ${
                        searchNode.queryFieldKey || "No query field"
                      } · ${
                        searchNode.catalogId
                          ? "Selected catalogue"
                          : "Default catalogue"
                      }`}
                    </option>
                  ))}
                </select>
                {form.searchNodeId && !selectedSearchNode && (
                  <p role="status" className="mt-1 text-xs text-amber-700">
                    The selected Search Catalogue node is no longer available in
                    this flow.
                  </p>
                )}
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Native product grouping
                </label>
                <select
                  aria-label="Present Catalogue Results Section Mode"
                  value={String(form.sectionMode || "flat")}
                  onChange={event =>
                    setForm(prev => ({ ...prev, sectionMode: event.target.value }))
                  }
                  className="w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500"
                >
                  {PRESENT_CATALOGUE_SECTION_MODES.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-slate-500">
                  Category grouping applies only when native product-list presentation is eligible.
                </p>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Prompt
                </label>
                <textarea
                  aria-label="Present Catalogue Results Prompt"
                  rows={3}
                  maxLength={PRESENT_CATALOGUE_RESULTS_MAXIMUM_PROMPT_LENGTH}
                  value={form.promptText}
                  onChange={event =>
                    setForm(prev => ({
                      ...prev,
                      promptText: event.target.value,
                    }))
                  }
                  placeholder={PRESENT_CATALOGUE_RESULTS_DEFAULT_PROMPT}
                  className="w-full resize-none rounded-md border px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500"
                />
                {renderCharHelper(
                  String(form.promptText || "").length,
                  PRESENT_CATALOGUE_RESULTS_MAXIMUM_PROMPT_LENGTH,
                  presentCatalogueResultsValidation?.reason === "prompt_too_long"
                    ? presentCatalogueResultsValidation.message
                    : ""
                )}
              </div>

              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-3">
                <p className="text-sm font-semibold text-slate-800">Commerce</p>
                <label className="mt-2 flex cursor-pointer items-start gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    aria-label="Add selected product or service to transaction"
                    checked={!!form.commerceEnabled}
                    onChange={event => {
                      const defaults = createPresentCatalogueCommerceConfig();
                      setForm(prev => ({
                        ...prev,
                        commerceEnabled: event.target.checked,
                        commerceTransactionType: defaults.transactionType,
                        commerceQuantityMode: defaults.quantityMode,
                        commerceQuantityPrompt: defaults.quantityPrompt,
                      }));
                    }}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span>Add selected product/service to transaction</span>
                </label>

                {form.commerceEnabled && (
                  <div className="mt-3 space-y-3 border-t border-slate-200 pt-3">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-slate-700">
                        Transaction type
                      </label>
                      <select
                        aria-label="Commerce Transaction Type"
                        value={form.commerceTransactionType}
                        onChange={event =>
                          setForm(prev => ({
                            ...prev,
                            commerceTransactionType: event.target.value,
                          }))
                        }
                        className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500"
                      >
                        {PRESENT_CATALOGUE_COMMERCE_TRANSACTION_TYPES.map(
                          option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          )
                        )}
                      </select>
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-medium text-slate-700">
                        Quantity
                      </label>
                      <select
                        aria-label="Commerce Quantity Mode"
                        value={form.commerceQuantityMode}
                        onChange={event => {
                          const quantityMode = event.target.value;
                          setForm(prev => ({
                            ...prev,
                            commerceQuantityMode: quantityMode,
                            commerceQuantityPrompt:
                              quantityMode === "ask_customer"
                                ? String(prev.commerceQuantityPrompt || "").trim() ||
                                  PRESENT_CATALOGUE_COMMERCE_DEFAULT_QUANTITY_PROMPT
                                : "",
                          }));
                        }}
                        className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500"
                      >
                        {PRESENT_CATALOGUE_COMMERCE_QUANTITY_MODES.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {form.commerceQuantityMode === "ask_customer" ? (
                      <div>
                        <label className="mb-1 block text-xs font-medium text-slate-700">
                          Quantity question
                        </label>
                        <textarea
                          aria-label="Commerce Quantity Question"
                          rows={2}
                          maxLength={
                            PRESENT_CATALOGUE_COMMERCE_MAXIMUM_QUANTITY_PROMPT_LENGTH
                          }
                          value={form.commerceQuantityPrompt}
                          onChange={event =>
                            setForm(prev => ({
                              ...prev,
                              commerceQuantityPrompt: event.target.value,
                            }))
                          }
                          className="w-full resize-none rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500"
                        />
                        <p className="mt-1 text-xs text-slate-500">
                          Customer will be asked for quantity after selecting a
                          product or service.
                        </p>
                        {renderCharHelper(
                          String(form.commerceQuantityPrompt || "").length,
                          PRESENT_CATALOGUE_COMMERCE_MAXIMUM_QUANTITY_PROMPT_LENGTH,
                          [
                            "quantity_prompt_required",
                            "quantity_prompt_too_long",
                          ].includes(presentCatalogueResultsValidation?.reason)
                            ? presentCatalogueResultsValidation.message
                            : ""
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-500">
                        Uses quantity 1 automatically.
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                <p className="text-xs font-semibold text-slate-700">Paths</p>
                <p className="mt-1 text-xs text-slate-500">
                  Selected · Unavailable
                </p>
              </div>
            </div>
          )}

          {isReviewAndSubmitNode && (
            <div className="space-y-5">
              <section>
                <label className="mb-1 block text-sm font-medium text-slate-700">
                  Review message
                </label>
                <textarea
                  aria-label="Review & Submit Message"
                  rows={3}
                  maxLength={REVIEW_AND_SUBMIT_MAXIMUM_PROMPT_LENGTH}
                  value={form.promptText}
                  onChange={event =>
                    setForm(prev => ({
                      ...prev,
                      promptText: event.target.value,
                    }))
                  }
                  placeholder={REVIEW_AND_SUBMIT_DEFAULT_PROMPT}
                  className="w-full resize-none rounded-md border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500"
                />
                <p className="mt-1 text-xs text-slate-500">
                  Sent before the customer confirms the request.
                </p>
                {renderCharHelper(
                  String(form.promptText || "").length,
                  REVIEW_AND_SUBMIT_MAXIMUM_PROMPT_LENGTH,
                  ["prompt_required", "prompt_too_long"].includes(
                    reviewAndSubmitValidation?.reason
                  )
                    ? reviewAndSubmitValidation.message
                    : ""
                )}
              </section>

              <section className="rounded-md border border-slate-200 bg-slate-50 p-3">
                <h3 className="text-sm font-semibold text-slate-800">Service pricing</h3>
                <p className="mt-1 text-xs text-slate-500">
                  Choose the server-verified amount shown in review and stored on the order.
                </p>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input type="radio" name="review-service-pricing" value="Catalogue"
                      checked={form.reviewServicePricingSource !== "DecisionTable"}
                      onChange={() => setForm(prev => ({ ...prev, reviewServicePricingSource: "Catalogue", reviewDecisionPriceBinding: "" }))} />
                    Catalogue price
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input type="radio" name="review-service-pricing" value="DecisionTable"
                      checked={form.reviewServicePricingSource === "DecisionTable"}
                      onChange={() => setForm(prev => ({ ...prev, reviewServicePricingSource: "DecisionTable" }))} />
                    Decision Table output
                  </label>
                </div>
                {form.reviewServicePricingSource === "DecisionTable" && (
                  <select aria-label="Decision Table price output"
                    className="mt-3 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                    value={form.reviewDecisionPriceBinding || ""}
                    onChange={event => setForm(prev => ({ ...prev, reviewDecisionPriceBinding: event.target.value }))}>
                    <option value="">Select an upstream Decimal output</option>
                    {getReviewDecisionTablePriceOutputs(flowNodes, flowEdges, node.id).map(output => (
                      <option key={`${output.nodeId}|${output.fieldKey}`} value={`${output.nodeId}|${output.fieldKey}`}>
                        {output.label} ({output.fieldKey})
                      </option>
                    ))}
                  </select>
                )}
              </section>

              <section className="rounded-md border border-slate-200 bg-slate-50 p-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-800">
                      Details to include
                    </h3>
                    <p className="mt-1 text-xs text-slate-500">
                      Choose information already captured earlier in this flow.
                    </p>
                  </div>
                  <span className="shrink-0 text-xs text-slate-400">
                    {(form.reviewDetailBindings || []).length}/
                    {REVIEW_AND_SUBMIT_MAXIMUM_BINDINGS}
                  </span>
                </div>

                <div className="mt-3">
                  <label className="sr-only" htmlFor="review-captured-field">
                    Add captured field
                  </label>
                  <select
                    id="review-captured-field"
                    aria-label="Add captured field"
                    value=""
                    disabled={
                      (form.reviewDetailBindings || []).length >=
                        REVIEW_AND_SUBMIT_MAXIMUM_BINDINGS ||
                      unboundReviewFields.length === 0
                    }
                    onChange={event => addReviewBinding(event.target.value)}
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
                  >
                    <option value="">
                      {unboundReviewFields.length
                        ? "+ Add captured field"
                        : "No eligible captured fields"}
                    </option>
                    {unboundReviewFields.map(field => (
                      <option
                        key={field.sourceInputCaptureNodeId}
                        value={field.sourceInputCaptureNodeId}
                      >
                        {`${field.label || "Input Capture"} · ${field.fieldKey} · ${field.inputType}`}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mt-3 space-y-2">
                  {(form.reviewDetailBindings || []).map((binding, index) => {
                    const sourceNode = flowNodes.find(
                      candidate =>
                        String(candidate?.id || "") ===
                        String(binding.sourceInputCaptureNodeId || "")
                    );
                    const sourceConfig = sourceNode?.data?.config || {};
                    const currentFieldKey = String(
                      sourceConfig.fieldKey || ""
                    )
                      .trim()
                      .toLowerCase();
                    const storedFieldKey = String(binding.fieldKey || "")
                      .trim()
                      .toLowerCase();
                    const missingSource =
                      !sourceNode || sourceNode.type !== "input_capture";
                    const staleFieldKey =
                      !missingSource &&
                      (!currentFieldKey || currentFieldKey !== storedFieldKey);
                    const sourceQuestion = String(
                      sourceConfig.question || ""
                    ).trim();

                    return (
                      <div
                        key={`${binding.sourceInputCaptureNodeId}-${index}`}
                        className={`rounded-md border bg-white p-3 ${
                          missingSource || staleFieldKey
                            ? "border-amber-300"
                            : "border-slate-200"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-slate-800">
                              {sourceQuestion ||
                                storedFieldKey ||
                                "Missing captured field"}
                            </p>
                            <p className="truncate text-xs text-slate-500">
                              {storedFieldKey || "No field key"}
                            </p>
                          </div>
                          <div className="flex shrink-0 items-center gap-1">
                            <button
                              type="button"
                              aria-label={`Move ${storedFieldKey} up`}
                              title="Move up"
                              disabled={index === 0}
                              onClick={() => moveReviewBinding(index, -1)}
                              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 disabled:opacity-30"
                            >
                              <ArrowUp className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              aria-label={`Move ${storedFieldKey} down`}
                              title="Move down"
                              disabled={
                                index ===
                                form.reviewDetailBindings.length - 1
                              }
                              onClick={() => moveReviewBinding(index, 1)}
                              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 disabled:opacity-30"
                            >
                              <ArrowDown className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              aria-label={`Remove ${storedFieldKey}`}
                              title="Remove"
                              onClick={() =>
                                updateReviewBindings(bindings =>
                                  bindings.filter(
                                    (_item, itemIndex) =>
                                      itemIndex !== index
                                  )
                                )
                              }
                              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-red-50 hover:text-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>

                        <label className="mt-3 block text-xs font-medium text-slate-700">
                          Display label
                          <input
                            aria-label={`Display label for ${storedFieldKey}`}
                            value={binding.labelOverride || ""}
                            maxLength={REVIEW_AND_SUBMIT_MAXIMUM_LABEL_LENGTH}
                            placeholder={
                              sourceQuestion || "Captured field label"
                            }
                            onChange={event =>
                              updateReviewBindings(bindings =>
                                bindings.map((item, itemIndex) =>
                                  itemIndex === index
                                    ? {
                                        ...item,
                                        labelOverride: event.target.value,
                                      }
                                    : item
                                )
                              )
                            }
                            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                          />
                        </label>
                        <div className="mt-1 text-right text-[11px] text-slate-400">
                          {String(binding.labelOverride || "").length}/
                          {REVIEW_AND_SUBMIT_MAXIMUM_LABEL_LENGTH}
                        </div>

                        {missingSource && (
                          <p role="alert" className="mt-2 text-xs text-amber-700">
                            This Input Capture node was removed. Remove or replace
                            this detail before saving.
                          </p>
                        )}
                        {staleFieldKey && (
                          <p role="alert" className="mt-2 text-xs text-amber-700">
                            This field key changed. Remove and add the captured
                            field again before saving.
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>

                {reviewAndSubmitValidation &&
                  !reviewAndSubmitValidation.isValid && (
                    <p role="alert" className="mt-3 text-xs text-red-600">
                      {reviewAndSubmitValidation.message}
                    </p>
                  )}
              </section>

              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                <p className="text-xs font-semibold text-slate-700">Paths</p>
                <p className="mt-1 text-xs text-slate-500">
                  Submitted · Change Requested · Cancelled
                </p>
              </div>
            </div>
          )}

          {/* Template node fields */}
          {isTemplateNode && (
            <div className="space-y-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select WhatsApp template
                </label>
                <Combobox
                  value={selectedTemplateOption}
                  onChange={tpl => {
                    const name = String(tpl?.name || "").trim();
                    setForm(prev => ({
                      ...prev,
                      templateName: name,
                      headerMediaUrl:
                        name && name === String(prev.templateName || "").trim()
                          ? prev.headerMediaUrl
                          : "",
                    }));
                    if (name) fetchFullTemplate(tpl);
                    else setSelectedTemplate(null);
                  }}
                  nullable
                >
                  <div className="relative">
                    <Combobox.Input
                      className="w-full border rounded-md px-3 py-2 text-sm pr-10 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      placeholder="Search & select template..."
                      displayValue={tpl => String(tpl?.name || "")}
                      onChange={e => setTemplateQuery(e.target.value)}
                    />
                    <Combobox.Button className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400">
                      ▾
                    </Combobox.Button>

                    <Combobox.Options className="fixed z-[1100] mt-1 max-h-72 w-[min(42rem,calc(100vw-4rem))] overflow-auto rounded-md border border-slate-200 bg-white py-1 text-sm shadow-xl">
                      {filteredTemplates.length === 0 ? (
                        <div className="px-3 py-2 text-slate-500">
                          No templates found
                        </div>
                      ) : (
                        filteredTemplates.map(tpl => {
                          const key = `${tpl.name}||${tpl.languageCode || ""}`;
                          return (
                            <Combobox.Option
                              key={key}
                              value={tpl}
                              className={({ active }) =>
                                `cursor-pointer select-none px-3 py-2 ${
                                  active
                                    ? "bg-emerald-50 text-emerald-900"
                                    : "text-slate-700"
                                }`
                              }
                            >
                              <div className="flex items-center justify-between gap-3">
                                <span className="truncate">{tpl.name}</span>
                                <span className="shrink-0 text-xs text-slate-400">
                                  {(tpl.languageCode || "").toString()}
                                </span>
                              </div>
                            </Combobox.Option>
                          );
                        })
                      )}
                    </Combobox.Options>
                  </div>
                </Combobox>
                <p className="text-[11px] text-gray-500 mt-1">
                  Templates are loaded from your synced approved template catalog.
                </p>
              </div>

              {/* Preview of template with buttons */}
              <WhatsAppTemplatePreview
                template={selectedTemplate}
                headerMediaUrl={form.headerMediaUrl}
                bodyParams={form.bodyParams}
                urlButtonParams={form.urlButtonParams}
                useProfileName={form.useProfileName}
                profileNameSlot={form.profileNameSlot}
              />

              {needsHeaderUrl && (
                <div className="rounded-lg border border-slate-200 bg-white p-2.5 space-y-3">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-medium text-slate-800">
                      {headerKindLabel} media
                    </label>
                    <span
                      className={`text-[11px] font-medium ${
                        !templateHeaderMediaValue || !templateHeaderMediaInvalid
                          ? "text-slate-500"
                          : "text-red-600"
                      }`}
                    >
                      {templateHeaderMediaInvalid
                        ? "Invalid media reference"
                        : "Required"}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="flex cursor-pointer items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-100">
                      <Upload className="h-3.5 w-3.5" />
                      {mediaUploading ? "Uploading..." : `Upload ${headerKindLabel}`}
                      <input
                        type="file"
                        className="hidden"
                        accept={acceptForHeaderKind(headerKind)}
                        onChange={handleTemplateHeaderMediaUpload}
                        disabled={mediaUploading}
                      />
                    </label>
                    {templateHeaderMediaValue.startsWith("handle:") ? (
                      <span className="flex items-center gap-1 rounded bg-emerald-100 px-2 py-1 text-xs text-emerald-800">
                        Uploaded
                        <button
                          type="button"
                          onClick={() => setForm(prev => ({ ...prev, headerMediaUrl: "" }))}
                          className="ml-1 text-red-500 hover:text-red-700"
                          title="Remove uploaded media"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </span>
                    ) : null}
                  </div>
                  <input
                    type="text"
                    inputMode="url"
                    value={form.headerMediaUrl}
                    onChange={e =>
                      setForm(prev => ({ ...prev, headerMediaUrl: e.target.value }))
                    }
                    className={`w-full border rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 ${
                      !templateHeaderMediaValue || !templateHeaderMediaInvalid
                        ? "border-slate-200 focus:ring-emerald-500"
                        : "border-red-400 focus:ring-red-500"
                    }`}
                    placeholder="https://... or uploaded media handle"
                  />
                  {templateHeaderMediaError ? (
                    <p className="text-[11px] text-red-600">{templateHeaderMediaError}</p>
                  ) : (
                    <p className="text-[11px] text-slate-500">
                      Upload a {headerKindLabel.toLowerCase()} or paste a public HTTPS URL.
                    </p>
                  )}
                </div>
              )}

              {bodyPlaceholderCount > 0 && (
                <div className="rounded-lg border border-slate-200 bg-white p-2.5 space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <label className="text-sm font-medium text-slate-800">
                      Body variables
                    </label>
                    <div className="flex items-center gap-3">
                      <span className="text-[11px] text-slate-500">
                        {bodyPlaceholderCount} var
                        {bodyPlaceholderCount === 1 ? "" : "s"}
                      </span>
                      {canUseProfile && (
                        <label className="flex items-center gap-2 text-[11px] text-slate-600 select-none">
                          <input
                            type="checkbox"
                            checked={!!form.useProfileName}
                            onChange={e => {
                              const checked = e.target.checked;
                              if (!checked) {
                                setForm(prev => ({ ...prev, useProfileName: false }));
                              } else {
                                const clamped = Math.max(
                                  1,
                                  Math.min(form.profileNameSlot || 1, bodyPlaceholderCount)
                                );
                                setForm(prev => ({
                                  ...prev,
                                  useProfileName: true,
                                  profileNameSlot: clamped,
                                }));
                              }
                            }}
                          />
                          Use contact name
                        </label>
                      )}
                    </div>
                  </div>

                  {form.useProfileName && (
                    <div className="flex items-center gap-2">
                      <div className="w-10 text-[11px] text-slate-500 shrink-0">
                        Slot
                      </div>
                      <select
                        className="w-full border rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        value={form.profileNameSlot || 1}
                        onChange={e => {
                          const n = parseInt(e.target.value, 10) || 1;
                          setForm(prev => ({
                            ...prev,
                            profileNameSlot: Math.max(
                              1,
                              Math.min(n, bodyPlaceholderCount)
                            ),
                          }));
                        }}
                      >
                        {Array.from(
                          { length: Math.max(bodyPlaceholderCount, 1) },
                          (_, i) => i + 1
                        ).map(n => (
                          <option key={n} value={n}>
                            {`{{${n}}}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="space-y-2">
                    {Array.from({ length: bodyPlaceholderCount }, (_, i) => {
                      const slot = i + 1;
                      const isProfileSlot =
                        !!form.useProfileName && (form.profileNameSlot || 1) === slot;
                      const value = Array.isArray(form.bodyParams)
                        ? form.bodyParams[i]
                        : "";

                      return (
                        <div key={slot} className="flex items-center gap-2">
                          <div className="w-10 text-[11px] text-slate-500 shrink-0">
                            {`{{${slot}}}`}
                          </div>
                          <input
                            className={`w-full border rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 ${
                              isProfileSlot
                                ? "border-slate-200 bg-slate-50 text-slate-600"
                                : "border-slate-200 focus:ring-emerald-500"
                            }`}
                            disabled={isProfileSlot}
                            value={
                              isProfileSlot ? "Contact name (auto)" : String(value || "")
                            }
                            placeholder={isProfileSlot ? "" : "Enter value"}
                            onChange={e => {
                              const next = Array.isArray(form.bodyParams)
                                ? [...form.bodyParams]
                                : Array.from(
                                    { length: bodyPlaceholderCount },
                                    () => ""
                                  );
                              next[i] = e.target.value;
                              setForm(prev => ({ ...prev, bodyParams: next }));
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>

                  <div className="text-[11px] leading-tight text-slate-500">
                    Fill values for template placeholders. You can auto-fill one
                    slot with the contact name.
                  </div>
                </div>
              )}

              {dynamicUrlButtons.length > 0 && (
                <div className="rounded-lg border border-slate-200 bg-white p-2.5 space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-slate-800">
                      URL button values
                    </label>
                    <span className="text-[11px] text-slate-500">
                      {dynamicUrlButtons.length} dynamic
                    </span>
                  </div>

                  <div className="space-y-2">
                    {dynamicUrlButtons.map(b => {
                      const idx = b.index;
                      const value = Array.isArray(form.urlButtonParams)
                        ? form.urlButtonParams[idx] || ""
                        : "";
                      const ok = String(value || "").trim().length > 0;
                      const label = b.text?.trim() || `Button ${idx + 1}`;

                      return (
                        <div key={`${idx}-${label}`} className="flex items-center gap-2">
                          <div className="w-16 text-[11px] text-slate-500 shrink-0">
                            {`Btn ${idx + 1}`}
                          </div>
                          <input
                            className={`w-full border rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 ${
                              ok
                                ? "border-slate-200 focus:ring-emerald-500"
                                : "border-red-400 focus:ring-red-500"
                            }`}
                            value={value}
                            placeholder={label}
                            onChange={e => {
                              const next = Array.isArray(form.urlButtonParams)
                                ? [...form.urlButtonParams]
                                : ["", "", ""];
                              next[idx] = e.target.value;
                              setForm(prev => ({ ...prev, urlButtonParams: next }));
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>

                  <div className="text-[11px] leading-tight text-slate-500">
                    Required only for templates with dynamic URL buttons (URL
                    contains {"{{1}}"}). Provide the placeholder value.
                  </div>
                </div>
              )}

              {false && (
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-slate-800">
                      Use contact name
                    </label>
                    <input
                      type="checkbox"
                      checked={!!form.useProfileName}
                      onChange={e => {
                        const checked = e.target.checked;
                        if (!checked) {
                          setForm(prev => ({
                            ...prev,
                            useProfileName: false,
                          }));
                        } else {
                          const clamped = Math.max(
                            1,
                            Math.min(form.profileNameSlot || 1, bodyPlaceholderCount)
                          );
                          setForm(prev => ({
                            ...prev,
                            useProfileName: true,
                            profileNameSlot: clamped,
                          }));
                        }
                      }}
                    />
                  </div>

                  {form.useProfileName && (
                    <div className="mt-2">
                      <label className="block text-[11px] text-slate-600 mb-1">
                        Slot ({"{{n}}"})
                      </label>
                      <select
                        className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        value={form.profileNameSlot || 1}
                        onChange={e => {
                          const n = parseInt(e.target.value, 10) || 1;
                          setForm(prev => ({
                            ...prev,
                            profileNameSlot: Math.max(
                              1,
                              Math.min(n, bodyPlaceholderCount)
                            ),
                          }));
                        }}
                      >
                        {Array.from(
                          { length: Math.max(bodyPlaceholderCount, 1) },
                          (_, i) => i + 1
                        ).map(n => (
                          <option key={n} value={n}>
                            {`{{${n}}}`}
                          </option>
                        ))}
                      </select>
                      <p className="mt-1 text-[11px] text-slate-500">
                        Fills the selected placeholder with the contactâ€™s WhatsApp
                        profile name (if available), otherwise the saved contact
                        name.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* 🆕 CTA Flow node fields */}
          {isCtaNode && (
            <div className="space-y-3">
              <label className="block text-sm font-semibold text-slate-700">
                Attach CTA Flow
              </label>
              <select
                value={form.ctaFlowConfigId}
                onChange={handleCtaFlowChange}
                className="w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 transition-colors focus:border-emerald-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
              >
                <option value="">-- Choose published CTA flow --</option>
                {ctaFlows.map(flow => (
                  <option key={flow.id} value={flow.id}>
                    {flow.flowName}
                  </option>
                ))}
              </select>
              <div className="rounded-lg bg-slate-50 p-3">
                <p className="text-[11px] leading-relaxed text-slate-500">
                  This flow is built in the CTA Flow Builder. When this
                  auto-reply fires and the user enters that journey, this flow
                  will run and log steps into CTA analytics.
                </p>
                {form.ctaFlowName && (
                  <p className="mt-2 text-[11px] font-medium text-emerald-700">
                    Selected flow: <span className="font-bold">{form.ctaFlowName}</span>
                  </p>
                )}
              </div>
            </div>
          )}

          {isHandoverNode && (
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-slate-700">
                Message before handover (optional)
              </label>
              <textarea
                name="ackText"
                value={form.ackText}
                onChange={handleChange}
                rows={3}
                className="w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm transition-colors focus:border-emerald-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
                placeholder="No message will be sent before handover."
              />
              <p className="text-[11px] text-slate-500">
                This message is sent before the conversation is transferred to a human agent. Clear the message if you do not want to send one. Handover ends this automation.
              </p>
            </div>
          )}

          {isLeadTemperatureNode && (
            <div className="space-y-5">
              <p className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">Updates the lead when the conversation reaches this node, then continues immediately. Place it after the customer response or successful form submission that indicates their interest.</p>
              <label className="block text-sm font-medium text-slate-700">Set lead temperature
                <select className="mt-2 h-9 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm font-normal" value={form.leadTemperature || "Warm"} onChange={event => setForm(previous => ({ ...previous, leadTemperature: event.target.value }))}>
                  <option value="Cold">Cold</option><option value="Warm">Warm</option><option value="Hot">Hot</option>
                </select>
              </label>
              <label className="flex items-start gap-2 text-sm text-slate-700"><input type="checkbox" className="mt-1" checked={form.allowTemperatureDowngrade === true} onChange={event => setForm(previous => ({ ...previous, allowTemperatureDowngrade: event.target.checked }))} /><span>Allow lowering an automatically set temperature<span className="mt-1 block text-xs text-slate-500">For example, changing Hot to Warm. Disabled by default.</span></span></label>
              <p className="text-xs text-slate-500">Manual temperatures are protected. Repeating the same value creates no additional temperature-change event. This node does not assign leads or send notifications.</p>
            </div>
          )}
          {/* Tag node fields */}
          {isTagNode && (
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-slate-700">
                Tags (comma separated)
              </label>
              <input
                type="text"
                value={form.tags.join(", ")}
                onChange={handleTagsChange}
                className="w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm transition-colors focus:border-emerald-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
                placeholder="e.g. new_lead, hot, whatsapp_inbound"
              />
            </div>
          )}

          {followUpEligible && (
            <AutoReplyFollowUpEditor
              value={followUp}
              onChange={setFollowUp}
              templates={templates}
              maxHorizonSeconds={followUpMaxHorizonSeconds}
            />
          )}

          {/* Wait node fields */}
          {isWaitNode && (
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-slate-700">
                Wait time (seconds)
              </label>
              <input
                type="number"
                name="seconds"
                min={1}
                value={form.seconds}
                onChange={handleChange}
                className="w-32 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm transition-colors focus:border-emerald-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
              <p className="text-[11px] text-slate-500">
                The flow will pause for this duration before moving to the next node.
              </p>
            </div>
          )}

        </div>

        {/* Pinned footer */}
        <div className="flex justify-end gap-3 border-t border-slate-100 bg-white px-5 py-4">
          <Button
            variant="outline"
            type="button"
            onClick={discardChangesAndClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900 border-slate-200"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={
              (isButtonChoiceNode && choiceValidation && !choiceValidation.isValid) ||
              (isUrlCtaNode && urlCtaValidation && !urlCtaValidation.isValid) ||
              (isInputCaptureNode && !validateInputCaptureConfig(form).isValid) ||
              (isDecisionTableNode && !validateDecisionTableConfig(form.decisionTable).isValid) ||
              (isBrowseCatalogueNode && browseCatalogueValidation && !browseCatalogueValidation.isValid) ||
              (isSearchCatalogueNode && searchCatalogueValidation && !searchCatalogueValidation.isValid) ||
              presentCatalogueResultsHasBlockingError ||
              (isMediaNode && (mediaUrlInvalid || mediaReferenceMissing)) ||
              (isTemplateNode && (templateHeaderMediaMissing || templateHeaderMediaInvalid))
              || Boolean(followUpValidationError)
            }
            className="bg-emerald-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-emerald-700"
          >
            Save Block
          </Button>
        </div>
        </div>
      </div>
      </div>
    </>
  );
}

